import React, { useState, useEffect, useRef, useContext, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import JSZip from 'jszip';
import AuthContext from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import {
    Sparkles, Download, Copy, Check, ChevronLeft, ChevronRight,
    Plus, Trash2, Image as ImageIcon, Palette, ArrowLeft,
    RefreshCw, Layers, Eye, MoveLeft, MoveRight,
    CopyPlus, Type, Quote, BarChart2, CheckCircle2,
    Shield, Zap, SlidersHorizontal, Wand2, Bot, Calendar,
    HelpCircle, Sparkle, Settings2, FileText, Newspaper, Pipette
} from 'lucide-react';
import { InstagramIcon } from '../components/Icons';

// Master Canvas Dimensions (Instagram Portrait 4:5)
const CANVAS_WIDTH = 1080;
const CANVAS_HEIGHT = 1350;

// Master Grid Alignment Constants (Pixel-Perfect Alignment across all slides)
const HEADER_X = 80;
const HEADER_Y = 110;
const BADGE_W = 120;
const BADGE_H = 50;
const BADGE_X = CANVAS_WIDTH - 80 - BADGE_W; // 880
const BADGE_Y = 85;
const BADGE_R = 25;
const COUNTER_TEXT_X = BADGE_X + (BADGE_W / 2); // 940
const COUNTER_TEXT_Y = HEADER_Y; // 110
const CONTENT_X = 80;
const CONTENT_W = CANVAS_WIDTH - (CONTENT_X * 2); // 920
const BOTTOM_INDICATOR_Y = CANVAS_HEIGHT - 90;
const BOTTOM_INDICATOR_RIGHT_X = CANVAS_WIDTH - 85;

// Color Math Utilities
function parseColorToHex(input) {
    if (!input || typeof input !== 'string') return null;
    const str = input.trim();
    if (str.startsWith('#')) {
        if (str.length === 4) {
            return '#' + str[1] + str[1] + str[2] + str[2] + str[3] + str[3];
        }
        return str;
    }
    // Space-separated RGB numbers e.g. "30 58 138" or "0 150 64"
    const spaceParts = str.split(/\s+/).map(Number);
    if (spaceParts.length >= 3 && !spaceParts.some(isNaN)) {
        const [r, g, b] = spaceParts;
        return '#' + [r, g, b].map(x => Math.round(Math.max(0, Math.min(255, x))).toString(16).padStart(2, '0')).join('');
    }
    // rgb(...) or rgba(...) format
    const rgbMatch = str.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
    if (rgbMatch) {
        const [, r, g, b] = rgbMatch.map(Number);
        return '#' + [r, g, b].map(x => Math.round(Math.max(0, Math.min(255, x))).toString(16).padStart(2, '0')).join('');
    }
    return null;
}

function hexToRgb(hex) {
    const c = hex.replace('#', '');
    return [parseInt(c.substring(0, 2), 16), parseInt(c.substring(2, 4), 16), parseInt(c.substring(4, 6), 16)];
}

function rgbToHex(r, g, b) {
    return '#' + [r, g, b].map(x => Math.round(Math.max(0, Math.min(255, x))).toString(16).padStart(2, '0')).join('');
}

function rgbToHsl(r, g, b) {
    r /= 255; g /= 255; b /= 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h = 0, s = 0, l = (max + min) / 2;
    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
            case g: h = ((b - r) / d + 2) / 6; break;
            case b: h = ((r - g) / d + 4) / 6; break;
            default: break;
        }
    }
    return [h * 360, s * 100, l * 100];
}

function hslToRgb(h, s, l) {
    h /= 360; s /= 100; l /= 100;
    let r, g, b;
    if (s === 0) { r = g = b = l; }
    else {
        const hue2rgb = (p, q, t) => {
            if (t < 0) t += 1; if (t > 1) t -= 1;
            if (t < 1/6) return p + (q - p) * 6 * t;
            if (t < 1/2) return q;
            if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
            return p;
        };
        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;
        r = hue2rgb(p, q, h + 1/3);
        g = hue2rgb(p, q, h);
        b = hue2rgb(p, q, h - 1/3);
    }
    return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
}

function hslToHex(h, s, l) {
    const [r, g, b] = hslToRgb(h, s, l);
    return rgbToHex(r, g, b);
}

// Helper: Get active Favicon / Logo from the browser tab
const getTabFavicon = () => {
    if (typeof document === 'undefined') return '';
    const link = document.querySelector("link[rel*='icon']") || document.querySelector("link[rel='apple-touch-icon']");
    if (link && link.href) {
        return link.href;
    }
    return '';
};

// Base Pfadfinder Themes (Clean, professional typography without emojis)
const BASE_THEMES = [
    // --- HELL / LIGHT THEMES (Optimiert für helle Bilder & Tageslicht) ---
    {
        id: 'sunlight_meadow',
        name: 'Sommerwiese & Sonne',
        subtitle: 'Hell, frisch & tageslichttauglich',
        isLight: true,
        bgColor: '#F5F9F6',
        cardBg: 'rgba(255, 255, 255, 0.96)',
        accentColor: '#16A34A',
        accentText: '#FFFFFF',
        tagBg: '#22C55E',
        textColor: '#0F172A',
        subtextColor: '#475569',
        borderColor: 'rgba(22, 163, 74, 0.25)'
    },
    {
        id: 'sand_campfire_light',
        name: 'Pergament & Sand',
        subtitle: 'Warme helle Naturtöne',
        isLight: true,
        bgColor: '#FAF6F0',
        cardBg: 'rgba(255, 255, 255, 0.96)',
        accentColor: '#D97706',
        accentText: '#FFFFFF',
        tagBg: '#B45309',
        textColor: '#1C1917',
        subtextColor: '#57534E',
        borderColor: 'rgba(217, 119, 6, 0.25)'
    },
    {
        id: 'alpine_sky_light',
        name: 'Himmel & Berggipfel',
        subtitle: 'Klares Hellblau & Gletscherweiß',
        isLight: true,
        bgColor: '#F0F7FA',
        cardBg: 'rgba(255, 255, 255, 0.96)',
        accentColor: '#0284C7',
        accentText: '#FFFFFF',
        tagBg: '#0369A1',
        textColor: '#0F172A',
        subtextColor: '#475569',
        borderColor: 'rgba(2, 132, 199, 0.25)'
    },
    {
        id: 'editorial_light',
        name: 'Modern Pure White',
        subtitle: 'Minimalistisches Magazin-Design',
        isLight: true,
        bgColor: '#F8FAFC',
        cardBg: 'rgba(255, 255, 255, 0.98)',
        accentColor: '#0F172A',
        accentText: '#FFFFFF',
        tagBg: '#334155',
        textColor: '#0F172A',
        subtextColor: '#64748B',
        borderColor: 'rgba(15, 23, 42, 0.15)'
    },

    // --- DUNKEL / DARK THEMES ---
    {
        id: 'pine',
        name: 'Wald & Wiese (Dunkel)',
        subtitle: 'Klassisches Dunkelgrün',
        isLight: false,
        bgColor: '#0B1B13',
        cardBg: 'rgba(18, 43, 31, 0.92)',
        accentColor: '#52B788',
        accentText: '#081C15',
        tagBg: '#2D6A4F',
        textColor: '#FFFFFF',
        subtextColor: '#D8F3DC',
        borderColor: 'rgba(82, 183, 136, 0.35)'
    },
    {
        id: 'campfire',
        name: 'Lagerfeuer & Glut (Dunkel)',
        subtitle: 'Warmes Bernstein & Glut',
        isLight: false,
        bgColor: '#160E0B',
        cardBg: 'rgba(38, 22, 16, 0.92)',
        accentColor: '#FF9E00',
        accentText: '#160E0B',
        tagBg: '#D00000',
        textColor: '#FFFFFF',
        subtextColor: '#FFDDD2',
        borderColor: 'rgba(255, 158, 0, 0.35)'
    },
    {
        id: 'royal_gold',
        name: 'Pfadfinder-Gold (Dunkel)',
        subtitle: 'Edles Dunkelgrün & Gold',
        isLight: false,
        bgColor: '#071811',
        cardBg: 'rgba(15, 34, 25, 0.94)',
        accentColor: '#F3C053',
        accentText: '#0B1B13',
        tagBg: '#8C6D1F',
        textColor: '#FFFFFF',
        subtextColor: '#FFF3D4',
        borderColor: 'rgba(243, 192, 83, 0.40)'
    },
    {
        id: 'midnight',
        name: 'Sternenhimmel & Jurte',
        subtitle: 'Tiefes Nachtblau & Eis',
        isLight: false,
        bgColor: '#090D16',
        cardBg: 'rgba(18, 26, 44, 0.92)',
        accentColor: '#00B4D8',
        accentText: '#090D16',
        tagBg: '#0077B6',
        textColor: '#FFFFFF',
        subtextColor: '#CAF0F8',
        borderColor: 'rgba(0, 180, 216, 0.35)'
    },
    {
        id: 'vintage',
        name: 'Leder & Pergament (Dunkel)',
        subtitle: 'Rustikale Naturtöne',
        isLight: false,
        bgColor: '#1A1410',
        cardBg: 'rgba(46, 34, 26, 0.92)',
        accentColor: '#DDB892',
        accentText: '#1F1712',
        tagBg: '#7F5539',
        textColor: '#FFFFFF',
        subtextColor: '#EDE0D4',
        borderColor: 'rgba(221, 184, 146, 0.35)'
    },
    {
        id: 'minimal_dark',
        name: 'Minimal Obsidian',
        subtitle: 'Moderner Dark Mode',
        isLight: false,
        bgColor: '#0A0A0C',
        cardBg: 'rgba(24, 24, 27, 0.94)',
        accentColor: '#FFFFFF',
        accentText: '#09090B',
        tagBg: '#3F3F46',
        textColor: '#FFFFFF',
        subtextColor: '#A1A1AA',
        borderColor: 'rgba(255, 255, 255, 0.25)'
    }
];

// Master Brand Header (Pixel-Identical on every slide)
function drawBrandHeader(ctx, x, y, logoImg, theme, stammName) {
    ctx.save();
    let textStartX = x;

    if (logoImg) {
        // Draw crisp logo badge container
        const badgeSize = 58;
        const badgeRadius = 14;

        ctx.save();
        ctx.shadowColor = theme.isLight ? 'rgba(0, 0, 0, 0.12)' : 'rgba(0, 0, 0, 0.45)';
        ctx.shadowBlur = 10;
        ctx.shadowOffsetY = 3;

        // Clean white backing badge for absolute logo contrast
        ctx.fillStyle = 'rgba(255, 255, 255, 0.98)';
        ctx.beginPath();
        ctx.roundRect(x - 4, y - badgeSize / 2 - 2, badgeSize + 8, badgeSize + 4, badgeRadius);
        ctx.fill();
        if (theme.isLight) {
            ctx.strokeStyle = 'rgba(0, 0, 0, 0.12)';
            ctx.lineWidth = 1;
            ctx.stroke();
        }
        ctx.restore();

        // Clip and draw logo image inside
        ctx.save();
        ctx.beginPath();
        ctx.roundRect(x, y - badgeSize / 2 + 1, badgeSize, badgeSize - 2, badgeRadius - 2);
        ctx.clip();

        const imgW = logoImg.width || badgeSize;
        const imgH = logoImg.height || badgeSize;
        const imgAspect = imgW / imgH;
        let dw = badgeSize;
        let dh = badgeSize;
        let dx = x;
        let dy = y - badgeSize / 2 + 1;

        if (imgAspect > 1) {
            dh = badgeSize / imgAspect;
            dy += (badgeSize - dh) / 2;
        } else {
            dw = badgeSize * imgAspect;
            dx += (badgeSize - dw) / 2;
        }

        try {
            ctx.drawImage(logoImg, dx, dy, dw, dh);
        } catch (err) {
            console.warn('Canvas draw logo error:', err);
        }
        ctx.restore();

        textStartX = x + badgeSize + 22;
    } else {
        // Clean accent indicator bar when no logo image is available
        ctx.fillStyle = theme.accentColor;
        ctx.beginPath();
        ctx.roundRect(x, y - 18, 6, 36, 3);
        ctx.fill();
        textStartX = x + 20;
    }

    // Render Stamm Name Text
    ctx.fillStyle = theme.textColor;
    ctx.font = 'bold 34px "Outfit", "Segoe UI", sans-serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(stammName.toUpperCase(), textStartX, y);

    ctx.restore();
}

// Master Slide Counter Badge (Pixel-Identical on all slides)
function drawSlideCounter(ctx, current, total, theme) {
    ctx.save();
    ctx.fillStyle = theme.isLight ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.12)';
    ctx.beginPath();
    ctx.roundRect(BADGE_X, BADGE_Y, BADGE_W, BADGE_H, BADGE_R);
    ctx.fill();
    ctx.strokeStyle = theme.borderColor;
    ctx.lineWidth = 1.5;
    ctx.stroke();

    ctx.fillStyle = theme.textColor;
    ctx.font = 'bold 24px "Outfit", "Segoe UI", sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(`${current + 1} / ${total}`, COUNTER_TEXT_X, COUNTER_TEXT_Y);
    ctx.restore();
}

// Master Outro Brand Emblem (Large Logo or Stamm Emblem)
function drawBrandOutro(ctx, cx, cy, logoImg, theme, stammName) {
    const size = 180;
    const radius = 36;

    if (logoImg) {
        ctx.save();
        ctx.shadowColor = theme.accentColor;
        ctx.shadowBlur = 30;

        // Circular / rounded white badge
        ctx.fillStyle = '#FFFFFF';
        ctx.beginPath();
        ctx.roundRect(cx - size / 2, cy - size / 2, size, size, radius);
        ctx.fill();
        ctx.strokeStyle = theme.borderColor;
        ctx.lineWidth = 4;
        ctx.stroke();
        ctx.restore();

        // Draw image
        ctx.save();
        ctx.beginPath();
        ctx.roundRect(cx - size / 2 + 12, cy - size / 2 + 12, size - 24, size - 24, radius - 8);
        ctx.clip();

        const imgW = logoImg.width || size;
        const imgH = logoImg.height || size;
        const imgAspect = imgW / imgH;
        let dw = size - 24;
        let dh = size - 24;
        let dx = cx - size / 2 + 12;
        let dy = cy - size / 2 + 12;

        if (imgAspect > 1) {
            dh = (size - 24) / imgAspect;
            dy += ((size - 24) - dh) / 2;
        } else {
            dw = (size - 24) * imgAspect;
            dx += ((size - 24) - dw) / 2;
        }

        try {
            ctx.drawImage(logoImg, dx, dy, dw, dh);
        } catch (err) {
            console.warn('Canvas draw outro logo error:', err);
        }
        ctx.restore();
    } else {
        // Clean typographic Stamm emblem
        ctx.save();
        ctx.fillStyle = theme.cardBg;
        ctx.beginPath();
        ctx.roundRect(cx - size / 2, cy - size / 2, size, size, radius);
        ctx.fill();
        ctx.strokeStyle = theme.accentColor;
        ctx.lineWidth = 4;
        ctx.stroke();

        ctx.fillStyle = theme.accentColor;
        ctx.font = '900 68px "Outfit", "Segoe UI", sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        const initials = stammName.split(' ').map(w => w[0]).join('').substring(0, 3).toUpperCase() || 'ST';
        ctx.fillText(initials, cx, cy);
        ctx.restore();
    }
}

// Helper: Wrap text with line limits and alignment
function drawWrappedText(ctx, text, x, y, maxWidth, lineHeight, maxLines = 10, align = 'left') {
    if (!text) return y;
    const words = String(text).split(' ');
    let line = '';
    let currentY = y;
    let lineCount = 0;
    ctx.textAlign = align;

    for (let n = 0; n < words.length; n++) {
        const testLine = line + words[n] + ' ';
        const metrics = ctx.measureText(testLine);
        if (metrics.width > maxWidth && n > 0) {
            lineCount++;
            if (lineCount >= maxLines) {
                ctx.fillText(line.trim() + '...', x, currentY);
                return currentY + lineHeight;
            }
            ctx.fillText(line.trim(), x, currentY);
            line = words[n] + ' ';
            currentY += lineHeight;
        } else {
            line = testLine;
        }
    }
    if (line.trim().length > 0) {
        ctx.fillText(line.trim(), x, currentY);
        return currentY + lineHeight;
    }
    return currentY;
}

// Helper: get array of lines wrapped
function getWrappedLines(ctx, text, maxWidth, maxLines = 10) {
    if (!text) return [];
    const words = String(text).split(' ');
    let lines = [];
    let currentLine = '';

    for (let n = 0; n < words.length; n++) {
        const testLine = currentLine + words[n] + ' ';
        const metrics = ctx.measureText(testLine);
        if (metrics.width > maxWidth && n > 0) {
            lines.push(currentLine.trim());
            if (lines.length >= maxLines) {
                lines[lines.length - 1] += '...';
                return lines;
            }
            currentLine = words[n] + ' ';
        } else {
            currentLine = testLine;
        }
    }
    if (currentLine.trim().length > 0) {
        lines.push(currentLine.trim());
    }
    return lines;
}

// Helper: draw image with cover aspect ratio & brightness filter
function drawImageCover(ctx, img, x, y, w, h, radius = 0, brightness = 100) {
    if (!img) return;
    const imgRatio = img.width / img.height;
    const targetRatio = w / h;
    let sWidth, sHeight, sx, sy;

    if (imgRatio > targetRatio) {
        sHeight = img.height;
        sWidth = img.height * targetRatio;
        sx = (img.width - sWidth) / 2;
        sy = 0;
    } else {
        sWidth = img.width;
        sHeight = img.width / targetRatio;
        sx = 0;
        sy = (img.height - sHeight) / 2;
    }

    ctx.save();
    if (radius > 0) {
        ctx.beginPath();
        ctx.roundRect(x, y, w, h, radius);
        ctx.clip();
    }
    if (brightness !== 100) {
        ctx.filter = `brightness(${brightness}%)`;
    }
    try {
        ctx.drawImage(img, sx, sy, sWidth, sHeight, x, y, w, h);
    } catch (err) {
        console.warn('Canvas drawImageCover error:', err);
    }
    ctx.restore();
}

const InstagramGenerator = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, API_URL, PUBLIC_API_URL } = useContext(AuthContext);
    const { organization, settings } = useSettings();

    const stateData = location.state || {};

    // Check if Bob / AI is strictly enabled
    const isBobEnabled = useMemo(() => {
        if (!settings) return false;
        if (settings?.ai?.enabled === false) return false;
        if (settings?.integrations?.ai?.enabled === false) return false;
        if (settings?.modules?.aiAssistant === false) return false;
        if (settings?.features?.bobChat === false && settings?.features?.aiAssistant === false) return false;
        if (settings?.ai_settings?.enabled === false) return false;
        return (settings?.ai?.enabled ?? settings?.integrations?.ai?.enabled ?? true) !== false && (settings?.modules?.aiAssistant !== false);
    }, [settings]);

    // Custom Override Color (if user manually tweaks Stamm color)
    const [customStammColor, setCustomStammColor] = useState('');

    // Dynamic Stamm Theme Generator (Uses exact primary/brand colors from tenant)
    const themes = useMemo(() => {
        let primaryHex = null;

        // 1. Direct custom user override if set
        if (customStammColor) {
            primaryHex = parseColorToHex(customStammColor);
        }

        // 2. Direct from settings object
        if (!primaryHex && settings?.colorTheme) {
            const ct = settings.colorTheme;
            primaryHex = parseColorToHex(ct.seed || ct.seedColor || ct.primary || ct.light?.primary || ct.dark?.primary);
        }

        // 3. From computed CSS variable --color-primary
        if (!primaryHex && typeof window !== 'undefined' && typeof document !== 'undefined') {
            const cssPrim = getComputedStyle(document.documentElement).getPropertyValue('--color-primary');
            primaryHex = parseColorToHex(cssPrim);
        }

        // Fallback default
        if (!primaryHex) {
            primaryHex = '#52B788';
        }

        const [r, g, b] = hexToRgb(primaryHex);
        const [h, s, l] = rgbToHsl(r, g, b);

        // Accent is vivid and bright for high contrast on dark backgrounds
        const accentColor = hslToHex(h, Math.min(s * 1.1, 95), 68);
        // Rich deep dark background with stamm hue
        const bgColor = hslToHex(h, Math.min(s * 0.4, 30), 6);
        // Deep translucent glass card
        const [cr, cg, cb] = hslToRgb(h, Math.min(s * 0.3, 25), 11);
        const cardBg = `rgba(${cr}, ${cg}, ${cb}, 0.94)`;
        const subtextColor = hslToHex(h, Math.min(s * 0.5, 40), 90);

        const dynamicStammDarkTheme = {
            id: 'stamm_custom',
            name: `${organization?.stammName || 'Stamm'}-Design (Dunkel)`,
            subtitle: `Dunkle Stammes-Farben (${primaryHex})`,
            isStammBrand: true,
            isLight: false,
            primaryHex,
            bgColor,
            cardBg,
            accentColor,
            accentText: bgColor,
            tagBg: primaryHex,
            textColor: '#FFFFFF',
            subtextColor,
            borderColor: `rgba(${r}, ${g}, ${b}, 0.40)`
        };

        const dynamicStammLightTheme = {
            id: 'stamm_light',
            name: `${organization?.stammName || 'Stamm'}-Design (Hell)`,
            subtitle: `Helle Stammes-Farben (${primaryHex})`,
            isStammBrand: true,
            isLight: true,
            primaryHex,
            bgColor: '#F4F7F4',
            cardBg: 'rgba(255, 255, 255, 0.96)',
            accentColor: primaryHex,
            accentText: '#FFFFFF',
            tagBg: primaryHex,
            textColor: '#0F172A',
            subtextColor: '#475569',
            borderColor: `rgba(${r}, ${g}, ${b}, 0.25)`
        };

        return [dynamicStammLightTheme, dynamicStammDarkTheme, ...BASE_THEMES];
    }, [organization, settings, customStammColor]);

    // Initial Tenant Defaults
    const defaultStamm = organization?.stammName || 'Pfadfinder Wutöschingen';
    const defaultWebsite = organization?.websiteUrl || (typeof window !== 'undefined' ? window.location.hostname.replace(/^dash\./, '') : 'pfadfinder-wutoeschingen.org');
    const defaultHandle = organization?.instagramUrl ? `@${organization.instagramUrl.replace(/.*instagram\.com\//, '').replace(/\/$/, '')}` : '@pfadfinder_wutoeschingen';
    
    // Auto-discover Logo from Tab Favicon or Settings
    const getInitialLogo = () => {
        return organization?.logoUrl || organization?.faviconUrl || settings?.organization?.logoUrl || settings?.organization?.faviconUrl || getTabFavicon();
    };

    const [stammName, setStammName] = useState(defaultStamm);
    const [websiteUrl, setWebsiteUrl] = useState(defaultWebsite);
    const [instagramHandle, setInstagramHandle] = useState(defaultHandle);
    const [logoUrl, setLogoUrl] = useState(getInitialLogo);
    const [themeId, setThemeId] = useState('stamm_light');
    const [themeFilter, setThemeFilter] = useState('all'); // 'all' | 'light' | 'dark'
    const [activeSlideIndex, setActiveSlideIndex] = useState(0);

    // Dynamic Settings & Tab Favicon Sync
    useEffect(() => {
        const tabFavicon = getTabFavicon();
        if (organization) {
            if (organization.stammName && organization.stammName !== 'Pfadfinder Stamm') {
                setStammName(prev => (!prev || prev === 'Pfadfinder Stamm' || prev === 'Pfadfinder Wutöschingen' ? organization.stammName : prev));
            }
            if (organization.websiteUrl) {
                setWebsiteUrl(prev => (!prev || prev.includes('pfadfinder-wutoeschingen.org') ? organization.websiteUrl : prev));
            }
            if (organization.instagramUrl) {
                const handle = `@${organization.instagramUrl.replace(/.*instagram\.com\//, '').replace(/\/$/, '')}`;
                setInstagramHandle(prev => (!prev || prev.includes('pfadfinder_wutoeschingen') ? handle : prev));
            }
            const foundLogo = organization.logoUrl || organization.faviconUrl || tabFavicon;
            if (foundLogo) {
                setLogoUrl(foundLogo);
            }
        } else if (tabFavicon) {
            setLogoUrl(tabFavicon);
        }
        if (settings?.organization?.logoUrl || settings?.organization?.faviconUrl) {
            const orgLogo = settings.organization.logoUrl || settings.organization.faviconUrl;
            if (orgLogo) setLogoUrl(orgLogo);
        }
    }, [organization, settings]);

    const [selectedBlogId, setSelectedBlogId] = useState('');
    const [selectedEventId, setSelectedEventId] = useState('');
    const [blogPosts, setBlogPosts] = useState([]);
    const [events, setEvents] = useState([]);
    const [loadingImports, setLoadingImports] = useState(false);

    // Bob AI Assistant Modal & States
    const [isAiModalOpen, setIsAiModalOpen] = useState(false);
    const [aiSourceMode, setAiSourceMode] = useState('topic'); // 'topic' | 'blog' | 'event'
    const [aiThemeStyle, setAiThemeStyle] = useState('light'); // 'light' | 'dark'
    const [aiPrompt, setAiPrompt] = useState('');
    const [aiSelectedBlogId, setAiSelectedBlogId] = useState('');
    const [aiSelectedEventId, setAiSelectedEventId] = useState('');
    const [aiIncludeStats, setAiIncludeStats] = useState(true);
    const [aiIncludeQuote, setAiIncludeQuote] = useState(true);
    const [aiIncludeEvent, setAiIncludeEvent] = useState(false);
    const [isAiGenerating, setIsAiGenerating] = useState(false);
    const [aiStatusMessage, setAiStatusMessage] = useState('');

    const [isExportingZip, setIsExportingZip] = useState(false);
    const [copiedCaption, setCopiedCaption] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [isUploadingLogo, setIsUploadingLogo] = useState(false);
    const [activeTab, setActiveTab] = useState('slides'); // 'slides' | 'branding' | 'caption'

    const [slides, setSlides] = useState(() => {
        if (stateData.slides && Array.isArray(stateData.slides) && stateData.slides.length > 0) {
            return stateData.slides;
        }

        const initialCover = {
            type: 'cover',
            layout: 'hero_full',
            title: stateData.title || 'Sommerlager 2026',
            subtitle: stateData.subtitle || '7 Tage voller Abenteuer, Natur & Lagerfeuer',
            imageUrl: stateData.imageUrl || (stateData.images && stateData.images[0]?.url) || '',
            tag: 'LAGERBERICHT',
            brightness: 100
        };

        const contentSlides = (stateData.images && stateData.images.length > 0)
            ? stateData.images.map((img, idx) => ({
                type: 'story',
                layout: idx % 2 === 0 ? 'full_card' : 'polaroid',
                title: img.title || `Highlight #${idx + 1}`,
                description: img.caption || 'Ein unvergesslicher Moment aus unserem Lageralltag.',
                imageUrl: typeof img === 'string' ? img : img.url,
                brightness: 100
            }))
            : [
                {
                    type: 'story',
                    layout: 'full_card',
                    title: 'Jurtenbau & Lagerfeuer',
                    description: 'Gemeinsam bauten wir unsere Schwarzzelter auf und entfachten das Abendfeuer.',
                    imageUrl: '',
                    brightness: 100
                },
                {
                    type: 'stats',
                    layout: 'stats_grid',
                    title: 'Das Lager in Zahlen',
                    subtitle: 'Eine ereignisreiche Woche im Schwarzwald',
                    stats: [
                        { label: 'Tage im Zelt', value: '7' },
                        { label: 'Gewandert', value: '85 km' },
                        { label: 'Pfadis & Leiter', value: '38' },
                        { label: 'Stockbrote', value: '120+' }
                    ],
                    imageUrl: '',
                    brightness: 100
                },
                {
                    type: 'quote',
                    layout: 'quote_center',
                    title: '“Wer Pfadfinder ist, findet immer einen Weg durchs Dickicht.”',
                    author: 'Stammesmotto 2026',
                    subtitle: 'Gemeinschaft, Verantwortung & echtes Abenteuer',
                    imageUrl: '',
                    brightness: 100
                }
            ];

        const initialOutro = {
            type: 'outro',
            layout: 'outro_shield',
            title: 'Lust auf dein eigenes Abenteuer?',
            subtitle: 'Werde Teil unserer Pfadfindergruppe! Wir treffen uns wöchentlich.',
            website: defaultWebsite,
            handle: defaultHandle,
            imageUrl: '',
            brightness: 100
        };

        return [initialCover, ...contentSlides, initialOutro];
    });

    const canvasRef = useRef(null);
    const imageCache = useRef(new Map());
    const activeTheme = themes.find(t => t.id === themeId) || themes[0];

    useEffect(() => {
        const fetchImports = async () => {
            setLoadingImports(true);
            try {
                const [blogRes, eventRes] = await Promise.allSettled([
                    axios.get(`${API_URL}/blog`),
                    axios.get(`${API_URL}/events`)
                ]);
                if (blogRes.status === 'fulfilled') {
                    const posts = Array.isArray(blogRes.value.data) ? blogRes.value.data : (blogRes.value.data?.posts || []);
                    setBlogPosts(posts);
                    if (posts.length > 0) setAiSelectedBlogId(posts[0]._id);
                }
                if (eventRes.status === 'fulfilled') {
                    const evts = Array.isArray(eventRes.value.data) ? eventRes.value.data : (eventRes.value.data?.events || []);
                    setEvents(evts);
                    if (evts.length > 0) setAiSelectedEventId(evts[0]._id);
                }
            } catch (err) {
                console.error('Failed to load imports for generator', err);
            } finally {
                setLoadingImports(false);
            }
        };
        fetchImports();
    }, [API_URL]);

    const handleSelectBlog = (blogId) => {
        setSelectedBlogId(blogId);
        setSelectedEventId('');
        const post = blogPosts.find(b => b._id === blogId);
        if (!post) return;

        let rawImages = Array.isArray(post.images) && post.images.length > 0
            ? post.images
            : (post.imageUrl ? [post.imageUrl] : []);

        const normalizedImages = rawImages.map(img => {
            if (typeof img === 'string') return { url: img, title: '', caption: '' };
            return { url: img?.url || '', title: img?.title || '', caption: img?.caption || '' };
        }).filter(img => img.url);

        const newCover = {
            type: 'cover',
            layout: 'hero_full',
            title: post.title || 'Neuer Bericht aus dem Stamm',
            subtitle: post.excerpt || 'Aktuelle Einblicke & Abenteuer aus unserem Stammesleben',
            imageUrl: normalizedImages[0]?.url || post.imageUrl || '',
            tag: 'RÜCKBLICK',
            brightness: 100
        };

        const storySlides = normalizedImages.map((img, idx) => ({
            type: 'story',
            layout: idx % 2 === 0 ? 'full_card' : 'polaroid',
            title: img.title || `Highlight #${idx + 1}`,
            description: img.caption || 'Erlebnisse aus unserem Stammesalltag in der Natur.',
            imageUrl: img.url,
            brightness: 100
        }));

        const outroSlide = {
            type: 'outro',
            layout: 'outro_shield',
            title: 'Lust auf Pfadfinden?',
            subtitle: 'Komm zu unseren Gruppenstunden vorbei oder schreib uns eine Nachricht!',
            website: websiteUrl,
            handle: instagramHandle,
            imageUrl: '',
            brightness: 100
        };

        setSlides([newCover, ...storySlides, outroSlide]);
        setActiveSlideIndex(0);
    };

    const handleSelectEvent = (eventId) => {
        setSelectedEventId(eventId);
        setSelectedBlogId('');
        const event = events.find(e => e._id === eventId);
        if (!event) return;

        const dateFormatted = event.date ? new Date(event.date).toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' }) : '';
        const eventCover = {
            type: 'cover',
            layout: 'hero_full',
            title: event.title || 'Kommende Aktion',
            subtitle: dateFormatted ? `Termin: ${dateFormatted}` : 'Sei dabei und erlebe Gemeinschaft!',
            imageUrl: event.imageUrl || '',
            tag: 'ANKÜNDIGUNG',
            brightness: 100
        };

        const eventDetail = {
            type: 'event',
            layout: 'event_badge',
            title: event.title || 'Event-Details',
            subtitle: event.description || 'Alle wichtigen Infos zu unserer nächsten Stammesaktion.',
            dateText: dateFormatted || 'Demnächst',
            locationText: event.location || 'Treffpunkt am Pfadiheim',
            targetGroup: event.targetGroup || 'Alle Altersstufen',
            imageUrl: event.imageUrl || '',
            brightness: 100
        };

        const outroSlide = {
            type: 'outro',
            layout: 'outro_shield',
            title: 'Jetzt anmelden & mitmachen!',
            subtitle: 'Alle Anmeldeunterlagen & Infos findest du auf unserer Website.',
            website: websiteUrl,
            handle: instagramHandle,
            imageUrl: '',
            brightness: 100
        };

        setSlides([eventCover, eventDetail, outroSlide]);
        setActiveSlideIndex(0);
    };

    // Resilient image loader supporting CORS, local assets & fallbacks
    const loadImage = useCallback((url) => {
        return new Promise((resolve) => {
            if (!url) {
                resolve(null);
                return;
            }
            if (imageCache.current.has(url)) {
                resolve(imageCache.current.get(url));
                return;
            }
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
                imageCache.current.set(url, img);
                resolve(img);
            };
            img.onerror = () => {
                // Fallback attempt without crossOrigin attribute
                const fallbackImg = new Image();
                fallbackImg.onload = () => {
                    imageCache.current.set(url, fallbackImg);
                    resolve(fallbackImg);
                };
                fallbackImg.onerror = () => {
                    console.warn('Could not load image for canvas:', url);
                    resolve(null);
                };
                fallbackImg.src = url;
            };
            img.src = url;
        });
    }, []);

    // Master Render Function: 100% Clean Modern Layout
    const renderSlideToContext = useCallback(async (ctx, slide, slideIndex, totalSlides, theme) => {
        ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

        ctx.fillStyle = theme.bgColor;
        ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

        const [img, logoImg] = await Promise.all([
            loadImage(slide.imageUrl),
            loadImage(logoUrl)
        ]);

        const brightness = slide.brightness !== undefined ? slide.brightness : 100;

        if (slide.type === 'cover') {
            if (img) {
                drawImageCover(ctx, img, 0, 0, CANVAS_WIDTH, CANVAS_HEIGHT, 0, brightness);
            } else {
                const bgGrad = ctx.createLinearGradient(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
                if (theme.isLight) {
                    bgGrad.addColorStop(0, '#FFFFFF');
                    bgGrad.addColorStop(0.5, theme.bgColor);
                    bgGrad.addColorStop(1, '#E2E8F0');
                } else {
                    bgGrad.addColorStop(0, theme.tagBg);
                    bgGrad.addColorStop(0.5, theme.bgColor);
                    bgGrad.addColorStop(1, '#05070A');
                }
                ctx.fillStyle = bgGrad;
                ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
            }

            const grad = ctx.createLinearGradient(0, 0, 0, CANVAS_HEIGHT);
            if (theme.isLight) {
                grad.addColorStop(0, 'rgba(255, 255, 255, 0.82)');
                grad.addColorStop(0.35, 'rgba(255, 255, 255, 0.40)');
                grad.addColorStop(0.60, 'rgba(255, 255, 255, 0.85)');
                grad.addColorStop(1, 'rgba(255, 255, 255, 0.98)');
            } else {
                grad.addColorStop(0, 'rgba(0,0,0,0.75)');
                grad.addColorStop(0.35, 'rgba(0,0,0,0.30)');
                grad.addColorStop(0.55, 'rgba(0,0,0,0.75)');
                grad.addColorStop(1, 'rgba(0,0,0,0.98)');
            }
            ctx.fillStyle = grad;
            ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

            // Master Header & Counter (Exact same coordinates)
            drawBrandHeader(ctx, HEADER_X, HEADER_Y, logoImg, theme, stammName);
            drawSlideCounter(ctx, slideIndex, totalSlides, theme);

            // Dynamic Upward Layout Engine (Guarantees Tag NEVER overlaps Title!)
            const titleRaw = slide.title || 'Titel des Beitrags';
            const subtitleRaw = slide.subtitle || '';
            const tagRaw = slide.tag || 'LAGERBERICHT';

            // Measure title font and wrap lines dynamically
            const titleFontSize = titleRaw.length > 42 ? 58 : (titleRaw.length > 26 ? 66 : 76);
            const titleLineHeight = titleFontSize * 1.16;
            ctx.font = `900 ${titleFontSize}px "Outfit", "Segoe UI", sans-serif`;
            const titleLines = getWrappedLines(ctx, titleRaw, CONTENT_W, 3);
            const totalTitleH = titleLines.length * titleLineHeight;

            // Measure subtitle
            ctx.font = '400 34px "Outfit", "Segoe UI", sans-serif';
            const subLineHeight = 44;
            const subtitleLines = subtitleRaw ? getWrappedLines(ctx, subtitleRaw, CONTENT_W - 140, 2) : [];
            const totalSubH = subtitleLines.length > 0 ? (subtitleLines.length * subLineHeight) : 0;

            // Anchor upwards from bottom margin
            const bottomMargin = 140;
            const contentBottomY = CANVAS_HEIGHT - bottomMargin;
            const subtitleStartY = contentBottomY - totalSubH;
            const titleStartY = subtitleStartY - totalTitleH - (subtitleLines.length > 0 ? 20 : 0);

            // 1. Render Tag Pill (Clean text pill without emoji clutter)
            if (tagRaw) {
                const tagPillH = 46;
                const tagPillY = titleStartY - tagPillH - 22;
                const tagText = tagRaw.toUpperCase();

                ctx.font = '900 20px "Outfit", "Segoe UI", sans-serif';
                const textMetrics = ctx.measureText(tagText);
                const tagPillW = Math.max(140, textMetrics.width + 48);

                ctx.fillStyle = theme.accentColor;
                ctx.beginPath();
                ctx.roundRect(CONTENT_X, tagPillY, tagPillW, tagPillH, 12);
                ctx.fill();

                ctx.fillStyle = theme.accentText;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText(tagText, CONTENT_X + tagPillW / 2, tagPillY + tagPillH / 2);
            }

            // 2. Render Title Lines
            ctx.fillStyle = theme.textColor;
            ctx.font = `900 ${titleFontSize}px "Outfit", "Segoe UI", sans-serif`;
            ctx.textAlign = 'left';
            ctx.textBaseline = 'top';
            titleLines.forEach((line, idx) => {
                ctx.fillText(line, CONTENT_X, titleStartY + (idx * titleLineHeight));
            });

            // 3. Render Subtitle Lines
            if (subtitleLines.length > 0) {
                ctx.fillStyle = theme.subtextColor;
                ctx.font = '400 34px "Outfit", "Segoe UI", sans-serif';
                ctx.textAlign = 'left';
                ctx.textBaseline = 'top';
                subtitleLines.forEach((line, idx) => {
                    ctx.fillText(line, CONTENT_X, subtitleStartY + (idx * subLineHeight));
                });
            }

            // 4. Swipe Hint
            ctx.fillStyle = theme.accentColor;
            ctx.font = 'bold 30px "Outfit", "Segoe UI", sans-serif';
            ctx.textAlign = 'right';
            ctx.textBaseline = 'middle';
            ctx.fillText('Wischen  ➔', BOTTOM_INDICATOR_RIGHT_X, BOTTOM_INDICATOR_Y);

        } else if (slide.type === 'story') {
            if (slide.layout === 'polaroid') {
                const frameX = CONTENT_X;
                const frameY = 180;
                const frameW = CONTENT_W;
                const frameH = 960;

                ctx.fillStyle = theme.isLight ? '#FFFFFF' : '#F8F9FA';
                ctx.beginPath();
                ctx.roundRect(frameX, frameY, frameW, frameH, 24);
                ctx.fill();
                ctx.strokeStyle = theme.isLight ? theme.borderColor : 'rgba(0,0,0,0.15)';
                ctx.lineWidth = 3;
                ctx.stroke();

                const photoPad = 36;
                const photoW = frameW - (photoPad * 2);
                const photoH = 640;
                if (img) {
                    drawImageCover(ctx, img, frameX + photoPad, frameY + photoPad, photoW, photoH, 14, brightness);
                } else {
                    ctx.fillStyle = theme.isLight ? '#F1F5F9' : '#E5E7EB';
                    ctx.beginPath();
                    ctx.roundRect(frameX + photoPad, frameY + photoPad, photoW, photoH, 14);
                    ctx.fill();
                }

                ctx.fillStyle = '#111827';
                ctx.font = 'bold 44px "Outfit", "Segoe UI", sans-serif';
                ctx.textAlign = 'left';
                ctx.textBaseline = 'alphabetic';
                const captionY = frameY + photoPad + photoH + 62;
                const nextCapY = drawWrappedText(ctx, slide.title || '', frameX + photoPad, captionY, photoW, 52, 2);

                ctx.fillStyle = '#4B5563';
                ctx.font = '400 30px "Outfit", "Segoe UI", sans-serif';
                drawWrappedText(ctx, slide.description || '', frameX + photoPad, nextCapY + 12, photoW, 40, 2);

                // Master Header & Counter (Exact same coordinates)
                drawBrandHeader(ctx, HEADER_X, HEADER_Y, logoImg, theme, stammName);
                drawSlideCounter(ctx, slideIndex, totalSlides, theme);

                ctx.fillStyle = theme.accentColor;
                ctx.font = 'bold 28px "Outfit", "Segoe UI", sans-serif';
                ctx.textAlign = 'right';
                ctx.textBaseline = 'middle';
                ctx.fillText('➔', BOTTOM_INDICATOR_RIGHT_X, BOTTOM_INDICATOR_Y);

            } else {
                if (img) {
                    drawImageCover(ctx, img, 0, 0, CANVAS_WIDTH, CANVAS_HEIGHT, 0, brightness);
                } else {
                    const bgGrad = ctx.createLinearGradient(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
                    if (theme.isLight) {
                        bgGrad.addColorStop(0, '#FFFFFF');
                        bgGrad.addColorStop(1, theme.bgColor);
                    } else {
                        bgGrad.addColorStop(0, theme.bgColor);
                        bgGrad.addColorStop(1, '#05070A');
                    }
                    ctx.fillStyle = bgGrad;
                    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
                }

                const grad = ctx.createLinearGradient(0, 0, 0, CANVAS_HEIGHT);
                if (theme.isLight) {
                    grad.addColorStop(0, 'rgba(255, 255, 255, 0.75)');
                    grad.addColorStop(0.35, 'rgba(255, 255, 255, 0.20)');
                    grad.addColorStop(0.55, 'rgba(255, 255, 255, 0.75)');
                    grad.addColorStop(1, 'rgba(255, 255, 255, 0.96)');
                } else {
                    grad.addColorStop(0, 'rgba(0,0,0,0.75)');
                    grad.addColorStop(0.35, 'rgba(0,0,0,0.15)');
                    grad.addColorStop(0.55, 'rgba(0,0,0,0.60)');
                    grad.addColorStop(1, 'rgba(0,0,0,0.98)');
                }
                ctx.fillStyle = grad;
                ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

                // Master Header & Counter (Exact same coordinates)
                drawBrandHeader(ctx, HEADER_X, HEADER_Y, logoImg, theme, stammName);
                drawSlideCounter(ctx, slideIndex, totalSlides, theme);

                const cardX = 60;
                const cardY = CANVAS_HEIGHT - 460;
                const cardW = CANVAS_WIDTH - 120;
                const cardH = 360;

                ctx.fillStyle = theme.cardBg;
                ctx.beginPath();
                ctx.roundRect(cardX, cardY, cardW, cardH, 32);
                ctx.fill();
                ctx.strokeStyle = theme.borderColor;
                ctx.lineWidth = 2;
                ctx.stroke();

                ctx.fillStyle = theme.accentColor;
                ctx.beginPath();
                ctx.roundRect(cardX + 40, cardY + 42, 60, 8, 4);
                ctx.fill();

                ctx.fillStyle = theme.textColor;
                ctx.font = 'bold 48px "Outfit", "Segoe UI", sans-serif';
                ctx.textAlign = 'left';
                ctx.textBaseline = 'alphabetic';
                const textStartY = cardY + 105;
                const nextY = drawWrappedText(ctx, slide.title || '', cardX + 40, textStartY, cardW - 80, 56, 2);

                ctx.fillStyle = theme.subtextColor;
                ctx.font = '400 32px "Outfit", "Segoe UI", sans-serif';
                drawWrappedText(ctx, slide.description || '', cardX + 40, nextY + 15, cardW - 80, 44, 3);
            }

        } else if (slide.type === 'quote') {
            const bgGrad = ctx.createLinearGradient(0, 0, 0, CANVAS_HEIGHT);
            if (theme.isLight) {
                bgGrad.addColorStop(0, '#FFFFFF');
                bgGrad.addColorStop(0.5, theme.bgColor);
                bgGrad.addColorStop(1, '#F1F5F9');
            } else {
                bgGrad.addColorStop(0, theme.bgColor);
                bgGrad.addColorStop(1, '#05070A');
            }
            ctx.fillStyle = bgGrad;
            ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

            // Master Header & Counter (Exact same coordinates)
            drawBrandHeader(ctx, HEADER_X, HEADER_Y, logoImg, theme, stammName);
            drawSlideCounter(ctx, slideIndex, totalSlides, theme);

            const boxX = CONTENT_X;
            const boxY = 220;
            const boxW = CONTENT_W;
            const boxH = CANVAS_HEIGHT - 360;

            ctx.fillStyle = theme.cardBg;
            ctx.beginPath();
            ctx.roundRect(boxX, boxY, boxW, boxH, 36);
            ctx.fill();
            ctx.strokeStyle = theme.borderColor;
            ctx.lineWidth = 2.5;
            ctx.stroke();

            ctx.fillStyle = theme.accentColor;
            ctx.font = '900 160px "Georgia", serif';
            ctx.textAlign = 'left';
            ctx.textBaseline = 'alphabetic';
            ctx.fillText('“', boxX + 50, boxY + 150);

            ctx.fillStyle = theme.textColor;
            ctx.font = 'bold 46px "Outfit", "Segoe UI", sans-serif';
            const quoteY = boxY + 210;
            const nextQuoteY = drawWrappedText(ctx, slide.title || 'Ein Pfadfinder lächelt und pfeift in allen Schwierigkeiten.', boxX + 60, quoteY, boxW - 120, 62, 5);

            ctx.strokeStyle = theme.borderColor;
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.moveTo(boxX + 60, nextQuoteY + 35);
            ctx.lineTo(boxX + 220, nextQuoteY + 35);
            ctx.stroke();

            ctx.fillStyle = theme.accentColor;
            ctx.font = 'bold 36px "Outfit", "Segoe UI", sans-serif';
            drawWrappedText(ctx, `— ${slide.author || 'Robert Baden-Powell'}`, boxX + 60, nextQuoteY + 88, boxW - 120, 44, 2);

            if (slide.subtitle) {
                ctx.fillStyle = theme.subtextColor;
                ctx.font = '400 28px "Outfit", "Segoe UI", sans-serif';
                drawWrappedText(ctx, slide.subtitle, boxX + 60, nextQuoteY + 138, boxW - 120, 36, 2);
            }

        } else if (slide.type === 'stats') {
            const bgGrad = ctx.createLinearGradient(0, 0, 0, CANVAS_HEIGHT);
            if (theme.isLight) {
                bgGrad.addColorStop(0, '#FFFFFF');
                bgGrad.addColorStop(0.5, theme.bgColor);
                bgGrad.addColorStop(1, '#F1F5F9');
            } else {
                bgGrad.addColorStop(0, theme.bgColor);
                bgGrad.addColorStop(1, '#05070A');
            }
            ctx.fillStyle = bgGrad;
            ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

            // Master Header & Counter (Exact same coordinates)
            drawBrandHeader(ctx, HEADER_X, HEADER_Y, logoImg, theme, stammName);
            drawSlideCounter(ctx, slideIndex, totalSlides, theme);

            ctx.fillStyle = theme.textColor;
            ctx.font = '900 60px "Outfit", "Segoe UI", sans-serif';
            ctx.textAlign = 'left';
            ctx.textBaseline = 'alphabetic';
            drawWrappedText(ctx, slide.title || 'Das Lager in Zahlen', CONTENT_X, 220, CONTENT_W, 70, 2);

            ctx.fillStyle = theme.subtextColor;
            ctx.font = '400 32px "Outfit", "Segoe UI", sans-serif';
            drawWrappedText(ctx, slide.subtitle || 'Einige Highlights & Fakten unseres Abenteuers', CONTENT_X, 290, CONTENT_W, 44, 2);

            const rawStats = slide.stats || [
                { label: 'Tage unterwegs', value: '7' },
                { label: 'Kilometer', value: '85 km' },
                { label: 'Teilnehmer', value: '42' },
                { label: 'Stockbrote', value: '150+' }
            ];

            const gridTop = 370;
            const gridPad = CONTENT_X;
            const cardGap = 30;
            const cardW = (CONTENT_W - cardGap) / 2;
            const cardH = 340;

            rawStats.slice(0, 4).forEach((st, idx) => {
                const row = Math.floor(idx / 2);
                const col = idx % 2;
                const cx = gridPad + col * (cardW + cardGap);
                const cy = gridTop + row * (cardH + cardGap);

                ctx.fillStyle = theme.cardBg;
                ctx.beginPath();
                ctx.roundRect(cx, cy, cardW, cardH, 28);
                ctx.fill();
                ctx.strokeStyle = theme.borderColor;
                ctx.lineWidth = 2;
                ctx.stroke();

                ctx.fillStyle = theme.accentColor;
                ctx.font = '900 72px "Outfit", "Segoe UI", sans-serif';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'alphabetic';
                ctx.fillText(st.value || '0', cx + cardW / 2, cy + 155);

                ctx.fillStyle = theme.textColor;
                ctx.font = 'bold 30px "Outfit", "Segoe UI", sans-serif';
                drawWrappedText(ctx, st.label || 'Kategorie', cx + cardW / 2, cy + 225, cardW - 40, 38, 2, 'center');
            });

        } else if (slide.type === 'event') {
            const bgGrad = ctx.createLinearGradient(0, 0, 0, CANVAS_HEIGHT);
            if (theme.isLight) {
                bgGrad.addColorStop(0, '#FFFFFF');
                bgGrad.addColorStop(0.5, theme.bgColor);
                bgGrad.addColorStop(1, '#F1F5F9');
            } else {
                bgGrad.addColorStop(0, theme.bgColor);
                bgGrad.addColorStop(1, '#05070A');
            }
            ctx.fillStyle = bgGrad;
            ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

            // Master Header & Counter (Exact same coordinates)
            drawBrandHeader(ctx, HEADER_X, HEADER_Y, logoImg, theme, stammName);
            drawSlideCounter(ctx, slideIndex, totalSlides, theme);

            ctx.fillStyle = theme.accentColor;
            ctx.beginPath();
            ctx.roundRect(CONTENT_X, 200, 290, 48, 12);
            ctx.fill();

            ctx.fillStyle = theme.accentText;
            ctx.font = '900 22px "Outfit", "Segoe UI", sans-serif';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(`TERMINANKÜNDIGUNG`, CONTENT_X + 145, 224);

            ctx.fillStyle = theme.textColor;
            ctx.font = '900 68px "Outfit", "Segoe UI", sans-serif';
            ctx.textAlign = 'left';
            ctx.textBaseline = 'alphabetic';
            const nextEvtY = drawWrappedText(ctx, slide.title || 'Sommerlager 2026', CONTENT_X, 325, CONTENT_W, 78, 2);

            const cardX = CONTENT_X;
            const cardY = nextEvtY + 40;
            const cardW = CONTENT_W;
            const cardH = 500;

            ctx.fillStyle = theme.cardBg;
            ctx.beginPath();
            ctx.roundRect(cardX, cardY, cardW, cardH, 32);
            ctx.fill();
            ctx.strokeStyle = theme.borderColor;
            ctx.lineWidth = 2;
            ctx.stroke();

            ctx.fillStyle = theme.accentColor;
            ctx.beginPath();
            ctx.roundRect(cardX + 40, cardY + 50, cardW - 80, 100, 20);
            ctx.fill();

            ctx.fillStyle = theme.accentText;
            ctx.font = '900 42px "Outfit", "Segoe UI", sans-serif';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(slide.dateText || '15. - 22. August 2026', cardX + cardW / 2, cardY + 100);

            ctx.fillStyle = theme.textColor;
            ctx.font = 'bold 36px "Outfit", "Segoe UI", sans-serif';
            ctx.textAlign = 'left';
            ctx.textBaseline = 'alphabetic';
            ctx.fillText(`Ort: ${slide.locationText || 'Pfadfinderheim'}`, cardX + 40, cardY + 220);
            ctx.fillText(`Zielgruppe: ${slide.targetGroup || 'Alle Altersstufen'}`, cardX + 40, cardY + 290);

            ctx.fillStyle = theme.subtextColor;
            ctx.font = '400 30px "Outfit", "Segoe UI", sans-serif';
            drawWrappedText(ctx, slide.subtitle || 'Packliste, Anmeldeschluss und alle Details findest du auf unserer Website.', cardX + 40, cardY + 365, cardW - 80, 42, 3);

        } else if (slide.type === 'outro') {
            if (img) {
                drawImageCover(ctx, img, 0, 0, CANVAS_WIDTH, CANVAS_HEIGHT, 0, brightness);
                const overlay = ctx.createLinearGradient(0, 0, 0, CANVAS_HEIGHT);
                if (theme.isLight) {
                    overlay.addColorStop(0, 'rgba(255, 255, 255, 0.85)');
                    overlay.addColorStop(0.35, 'rgba(255, 255, 255, 0.65)');
                    overlay.addColorStop(0.7, 'rgba(255, 255, 255, 0.90)');
                    overlay.addColorStop(1, 'rgba(255, 255, 255, 0.98)');
                } else {
                    overlay.addColorStop(0, 'rgba(0,0,0,0.80)');
                    overlay.addColorStop(0.35, 'rgba(0,0,0,0.65)');
                    overlay.addColorStop(0.7, 'rgba(0,0,0,0.88)');
                    overlay.addColorStop(1, 'rgba(0,0,0,0.98)');
                }
                ctx.fillStyle = overlay;
                ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
            } else {
                const bgGrad = ctx.createLinearGradient(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
                if (theme.isLight) {
                    bgGrad.addColorStop(0, '#FFFFFF');
                    bgGrad.addColorStop(0.5, theme.bgColor);
                    bgGrad.addColorStop(1, '#F1F5F9');
                } else {
                    bgGrad.addColorStop(0, theme.bgColor);
                    bgGrad.addColorStop(0.5, '#0d1512');
                    bgGrad.addColorStop(1, '#05070A');
                }
                ctx.fillStyle = bgGrad;
                ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
            }

            // Master Header & Counter (Pixel-Identical across all slides in the carousel!)
            drawBrandHeader(ctx, HEADER_X, HEADER_Y, logoImg, theme, stammName);
            drawSlideCounter(ctx, slideIndex, totalSlides, theme);

            // Tag Pill: SCHLUSS & KONTAKT
            const tagRaw = slide.tag || 'SCHLUSS & KONTAKT';
            const tagText = tagRaw.toUpperCase();
            ctx.font = '900 20px "Outfit", "Segoe UI", sans-serif';
            const textMetrics = ctx.measureText(tagText);
            const tagPillW = Math.max(160, textMetrics.width + 48);
            const tagPillH = 46;
            const tagPillY = 200;

            ctx.fillStyle = theme.accentColor;
            ctx.beginPath();
            ctx.roundRect(CONTENT_X, tagPillY, tagPillW, tagPillH, 12);
            ctx.fill();

            ctx.fillStyle = theme.accentText;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(tagText, CONTENT_X + tagPillW / 2, tagPillY + tagPillH / 2);

            // Outro Headline
            ctx.fillStyle = theme.textColor;
            ctx.font = '900 64px "Outfit", "Segoe UI", sans-serif';
            ctx.textAlign = 'left';
            ctx.textBaseline = 'alphabetic';
            const nextY = drawWrappedText(ctx, slide.title || 'Lust auf dein eigenes Abenteuer?', CONTENT_X, 325, CONTENT_W, 74, 2);

            // Outro Subtitle
            ctx.fillStyle = theme.subtextColor;
            ctx.font = '400 32px "Outfit", "Segoe UI", sans-serif';
            const afterSubY = drawWrappedText(ctx, slide.subtitle || 'Werde Teil unseres Stammes! Schreib uns einfach eine Nachricht oder besuche unsere Website.', CONTENT_X, nextY + 30, CONTENT_W, 44, 3);

            // Outro Contact & Action Card
            const cardX = CONTENT_X;
            const cardY = Math.max(afterSubY + 35, CANVAS_HEIGHT - 490);
            const cardW = CONTENT_W;
            const cardH = 390;

            ctx.fillStyle = theme.cardBg;
            ctx.beginPath();
            ctx.roundRect(cardX, cardY, cardW, cardH, 32);
            ctx.fill();
            ctx.strokeStyle = theme.borderColor;
            ctx.lineWidth = 2.5;
            ctx.stroke();

            const boxBgColor = theme.isLight ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.06)';

            // Website Box
            const webBoxY = cardY + 35;
            const boxInnerW = cardW - 60;
            const boxInnerH = 140;
            ctx.fillStyle = boxBgColor;
            ctx.beginPath();
            ctx.roundRect(cardX + 30, webBoxY, boxInnerW, boxInnerH, 20);
            ctx.fill();
            ctx.strokeStyle = theme.borderColor;
            ctx.lineWidth = 1.5;
            ctx.stroke();

            ctx.fillStyle = theme.subtextColor;
            ctx.font = 'bold 20px "Outfit", "Segoe UI", sans-serif';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText('WEBSITE & ANMELDUNG', cardX + cardW / 2, webBoxY + 42);

            ctx.fillStyle = theme.textColor;
            ctx.font = 'bold 36px "Outfit", "Segoe UI", sans-serif';
            ctx.fillText(slide.website || websiteUrl, cardX + cardW / 2, webBoxY + 92);

            // Instagram Box
            const instaBoxY = cardY + 200;
            ctx.fillStyle = boxBgColor;
            ctx.beginPath();
            ctx.roundRect(cardX + 30, instaBoxY, boxInnerW, boxInnerH, 20);
            ctx.fill();
            ctx.strokeStyle = theme.borderColor;
            ctx.lineWidth = 1.5;
            ctx.stroke();

            ctx.fillStyle = theme.subtextColor;
            ctx.font = 'bold 20px "Outfit", "Segoe UI", sans-serif';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText('INSTAGRAM', cardX + cardW / 2, instaBoxY + 42);

            ctx.fillStyle = theme.accentColor;
            ctx.font = 'bold 36px "Outfit", "Segoe UI", sans-serif';
            const handleText = slide.handle || instagramHandle;
            ctx.fillText(handleText.startsWith('@') ? handleText : `@${handleText}`, cardX + cardW / 2, instaBoxY + 92);
        }
    }, [stammName, websiteUrl, instagramHandle, logoUrl, loadImage]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        const currentSlide = slides[activeSlideIndex] || slides[0];
        if (currentSlide) {
            renderSlideToContext(ctx, currentSlide, activeSlideIndex, slides.length, activeTheme);
        }
    }, [slides, activeSlideIndex, activeTheme, renderSlideToContext]);

    // Handle Image Upload for active slide
    const handleSlideImageUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setIsUploading(true);
        const formData = new FormData();
        formData.append('file', file);

        try {
            const config = {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: `Bearer ${user.token}`,
                },
            };
            const uploadUrl = PUBLIC_API_URL ? `${PUBLIC_API_URL}/files/upload` : `${API_URL}/files/upload`;
            const res = await axios.post(uploadUrl, formData, config);
            const uploadedUrl = res.data.url;

            setSlides(prev => prev.map((s, idx) => idx === activeSlideIndex ? { ...s, imageUrl: uploadedUrl } : s));
        } catch (err) {
            console.error('Image upload failed in Instagram Generator:', err);
            alert('Fehler beim Hochladen des Bildes.');
        } finally {
            setIsUploading(false);
        }
    };

    // Handle Logo Upload
    const handleLogoUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setIsUploadingLogo(true);
        const formData = new FormData();
        formData.append('file', file);

        try {
            const config = {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: `Bearer ${user.token}`,
                },
            };
            const uploadUrl = PUBLIC_API_URL ? `${PUBLIC_API_URL}/files/upload` : `${API_URL}/files/upload`;
            const res = await axios.post(uploadUrl, formData, config);
            const uploadedUrl = res.data.url;

            setLogoUrl(uploadedUrl);
        } catch (err) {
            console.error('Logo upload failed:', err);
            alert('Fehler beim Hochladen des Logos.');
        } finally {
            setIsUploadingLogo(false);
        }
    };

    // Force refresh logo from browser tab favicon
    const handleReloadTabFavicon = () => {
        const tabFavicon = getTabFavicon();
        if (tabFavicon) {
            setLogoUrl(tabFavicon);
        } else {
            alert('Kein Favicon im Browser-Tab gefunden.');
        }
    };

    const handleAddSlide = (type = 'story') => {
        let newSlide;
        if (type === 'quote') {
            newSlide = {
                type: 'quote',
                layout: 'quote_center',
                title: '“Das schönste Gefühl beim Pfadfinden ist das gemeinsame Singen am Lagerfeuer.”',
                author: 'Sippenleiter',
                subtitle: 'Gemeinschaft, Abenteuer & Mut',
                imageUrl: '',
                brightness: 100
            };
        } else if (type === 'stats') {
            newSlide = {
                type: 'stats',
                layout: 'stats_grid',
                title: 'Aktion in Zahlen',
                subtitle: 'Unsere Erlebnisse auf einen Blick',
                stats: [
                    { label: 'Tage', value: '4' },
                    { label: 'Kilometer', value: '45 km' },
                    { label: 'Teilnehmer', value: '25' },
                    { label: 'Highlights', value: '100%' }
                ],
                imageUrl: '',
                brightness: 100
            };
        } else if (type === 'event') {
            newSlide = {
                type: 'event',
                layout: 'event_badge',
                title: 'Nächste Aktion',
                subtitle: 'Sei dabei und erlebe dein nächstes Abenteuer mit uns!',
                dateText: 'Kommender Samstag, 14:00 Uhr',
                locationText: 'Pfadiheim',
                targetGroup: 'Alle Altersstufen',
                imageUrl: '',
                brightness: 100
            };
        } else {
            newSlide = {
                type: 'story',
                layout: 'full_card',
                title: `Highlight #${slides.length}`,
                description: 'Kurze Beschreibung dieses Moments.',
                imageUrl: '',
                brightness: 100
            };
        }

        const hasOutro = slides[slides.length - 1]?.type === 'outro';
        if (hasOutro) {
            const updated = [...slides.slice(0, -1), newSlide, slides[slides.length - 1]];
            setSlides(updated);
            setActiveSlideIndex(updated.length - 2);
        } else {
            const updated = [...slides, newSlide];
            setSlides(updated);
            setActiveSlideIndex(updated.length - 1);
        }
    };

    const handleMoveSlide = (fromIndex, toIndex) => {
        if (toIndex < 0 || toIndex >= slides.length) return;
        setSlides(prev => {
            const copy = [...prev];
            const [moved] = copy.splice(fromIndex, 1);
            copy.splice(toIndex, 0, moved);
            return copy;
        });
        setActiveSlideIndex(toIndex);
    };

    const handleDuplicateSlide = (idx) => {
        const slideToDuplicate = slides[idx];
        if (!slideToDuplicate) return;
        const duplicated = { ...slideToDuplicate, title: `${slideToDuplicate.title || 'Folie'} (Kopie)` };
        setSlides(prev => {
            const copy = [...prev];
            copy.splice(idx + 1, 0, duplicated);
            return copy;
        });
        setActiveSlideIndex(idx + 1);
    };

    const handleRemoveSlide = (idxToRemove) => {
        if (slides.length <= 2) {
            alert('Ein Karussell benötigt mindestens 2 Folien.');
            return;
        }
        const updated = slides.filter((_, idx) => idx !== idxToRemove);
        setSlides(updated);
        setActiveSlideIndex(Math.max(0, idxToRemove - 1));
    };

    const updateActiveSlide = (field, value) => {
        setSlides(prev => prev.map((s, idx) => idx === activeSlideIndex ? { ...s, [field]: value } : s));
    };

    const updateStatItem = (statIndex, field, value) => {
        setSlides(prev => prev.map((s, idx) => {
            if (idx !== activeSlideIndex) return s;
            const curStats = Array.isArray(s.stats) ? [...s.stats] : [];
            if (!curStats[statIndex]) curStats[statIndex] = { label: '', value: '' };
            curStats[statIndex] = { ...curStats[statIndex], [field]: value };
            return { ...s, stats: curStats };
        }));
    };

    const handleDownloadSingle = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const link = document.createElement('a');
        link.download = `instagram_slide_${String(activeSlideIndex + 1).padStart(2, '0')}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
    };

    const handleDownloadAllZip = async () => {
        setIsExportingZip(true);
        try {
            const zip = new JSZip();
            const offCanvas = document.createElement('canvas');
            offCanvas.width = CANVAS_WIDTH;
            offCanvas.height = CANVAS_HEIGHT;
            const offCtx = offCanvas.getContext('2d');

            for (let i = 0; i < slides.length; i++) {
                await renderSlideToContext(offCtx, slides[i], i, slides.length, activeTheme);
                const dataUrl = offCanvas.toDataURL('image/png');
                const base64Data = dataUrl.replace(/^data:image\/png;base64,/, '');
                const fileName = `slide_${String(i + 1).padStart(2, '0')}_${slides[i].type}.png`;
                zip.file(fileName, base64Data, { base64: true });
            }

            const captionText = generateCaption();
            zip.file('instagram_caption.txt', captionText);

            const content = await zip.generateAsync({ type: 'blob' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(content);
            link.download = `instagram_karussell_${slides[0]?.title?.toLowerCase().replace(/[^a-z0-9]/g, '_') || 'pfadfinder'}.zip`;
            link.click();
        } catch (err) {
            console.error('Failed to create ZIP package', err);
            alert('Fehler beim Erstellen der ZIP-Datei.');
        } finally {
            setIsExportingZip(false);
        }
    };

    // Clean, professional caption without emoji spam
    const generateCaption = () => {
        const cover = slides[0] || {};
        const storySlides = slides.filter(s => s.type === 'story');
        const quoteSlides = slides.filter(s => s.type === 'quote');
        const statSlides = slides.filter(s => s.type === 'stats');
        const eventSlides = slides.filter(s => s.type === 'event');

        let text = `${cover.title?.toUpperCase() || 'NEUER BEITRAG'}\n`;
        if (cover.subtitle) text += `${cover.subtitle}\n`;
        text += `\n─────────────────────\n\n`;

        if (storySlides.length > 0) {
            text += `HIGHLIGHTS:\n`;
            storySlides.forEach((s, idx) => {
                if (s.title) {
                    text += `• ${s.title}${s.description ? `: ${s.description}` : ''}\n`;
                }
            });
            text += `\n`;
        }

        if (statSlides.length > 0 && statSlides[0]?.stats) {
            text += `FAKTEN:\n`;
            statSlides[0].stats.forEach(st => {
                if (st.value && st.label) {
                    text += `• ${st.value}: ${st.label}\n`;
                }
            });
            text += `\n`;
        }

        if (eventSlides.length > 0) {
            const ev = eventSlides[0];
            text += `TERMIN & INFOS:\n`;
            if (ev.dateText) text += `• Wann: ${ev.dateText}\n`;
            if (ev.locationText) text += `• Wo: ${ev.locationText}\n`;
            if (ev.targetGroup) text += `• Wer: ${ev.targetGroup}\n`;
            text += `\n`;
        }

        if (quoteSlides.length > 0 && quoteSlides[0]?.title) {
            text += `„${quoteSlides[0].title}“ — ${quoteSlides[0].author || 'Pfadfinder'}\n\n`;
        }

        text += `Lust auf Pfadfinden? Komm zu unseren wöchentlichen Gruppenstunden oder schreib uns eine Nachricht.\n\n`;
        text += `Mehr Infos auf unserer Website:\nhttps://${websiteUrl}\n\n`;

        const cleanStammTag = stammName.toLowerCase().replace(/[^a-z0-9]/g, '');
        text += `#pfadfinder #${cleanStammTag} #scouts #abenteuer #jugendgruppe #naturerleben #draussenzuhause #pfadfinden #outdoor`;

        return text;
    };

    const handleCopyCaption = () => {
        const text = generateCaption();
        navigator.clipboard.writeText(text);
        setCopiedCaption(true);
        setTimeout(() => setCopiedCaption(false), 2500);
    };

    // ==========================================
    // BOB AI ASSISTANT & MULTI-SOURCE ENGINE
    // ==========================================
    const handleRunBobAiGenerator = async (customSource = null, sourceId = null) => {
        if (!isBobEnabled) return;
        const effectiveSource = customSource || aiSourceMode;
        let promptToSend = aiPrompt.trim();
        let blogData = null;
        let eventData = null;

        if (effectiveSource === 'blog') {
            const targetBlogId = sourceId || aiSelectedBlogId || selectedBlogId;
            const post = blogPosts.find(b => b._id === targetBlogId);
            if (!post) {
                alert('Bitte wähle einen Blogbeitrag aus.');
                return;
            }
            blogData = {
                title: post.title,
                content: post.content,
                excerpt: post.excerpt,
                images: Array.isArray(post.images) ? post.images.map(img => typeof img === 'string' ? img : img?.url).filter(Boolean) : (post.imageUrl ? [post.imageUrl] : [])
            };
            if (!promptToSend) promptToSend = post.title;
        } else if (effectiveSource === 'event') {
            const targetEventId = sourceId || aiSelectedEventId || selectedEventId;
            const event = events.find(e => e._id === targetEventId);
            if (!event) {
                alert('Bitte wähle ein Event aus.');
                return;
            }
            const dateFormatted = event.date ? new Date(event.date).toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' }) : '';
            eventData = {
                title: event.title,
                description: event.description,
                date: dateFormatted,
                location: event.location,
                targetGroup: event.targetGroup,
                imageUrl: event.imageUrl
            };
            if (!promptToSend) promptToSend = event.title;
        }

        if (!promptToSend) {
            alert('Bitte gib ein Thema oder eine Beschreibung ein.');
            return;
        }

        setIsAiGenerating(true);
        setAiStatusMessage(`Bob generiert Karussell aus ${effectiveSource === 'blog' ? 'Blogbeitrag' : (effectiveSource === 'event' ? 'Event' : 'Vorgabe')}...`);

        try {
            const config = {
                headers: {
                    Authorization: `Bearer ${user.token}`,
                },
            };

            let generatedData = null;
            try {
                const res = await axios.post(`${API_URL}/ai/generate-instagram`, {
                    prompt: promptToSend,
                    sourceType: effectiveSource,
                    blogData,
                    eventData,
                    includeStats: aiIncludeStats,
                    includeQuote: aiIncludeQuote,
                    includeEvent: aiIncludeEvent,
                    stammName,
                    websiteUrl,
                    instagramHandle,
                    currentSlides: slides
                }, config);

                if (res.data && res.data.slides && Array.isArray(res.data.slides)) {
                    generatedData = res.data;
                }
            } catch (apiErr) {
                console.warn('Backend AI route failed or offline, using Bob intelligent client-side engine:', apiErr);
            }

            // Client-side intelligent engine fallback
            if (!generatedData) {
                let themeChosen = 'stamm_custom';
                let newSlides = [];

                if (effectiveSource === 'blog' && blogData) {
                    const blogImages = blogData.images || [];
                    newSlides.push({
                        type: 'cover',
                        layout: 'hero_full',
                        title: blogData.title || 'Neuer Bericht aus dem Stamm',
                        subtitle: blogData.excerpt || 'Spannende Einblicke & Abenteuer aus unserem Stammesleben',
                        tag: 'RÜCKBLICK',
                        imageUrl: blogImages[0] || '',
                        brightness: 100
                    });

                    newSlides.push({
                        type: 'story',
                        layout: 'full_card',
                        title: 'Zusammenhalt & Naturerlebnis',
                        description: blogData.excerpt || 'Gemeinsam erlebten wir unvergessliche Tage in der Natur.',
                        imageUrl: blogImages[1] || blogImages[0] || '',
                        brightness: 100
                    });

                    if (aiIncludeStats) {
                        newSlides.push({
                            type: 'stats',
                            layout: 'stats_grid',
                            title: 'Die Aktion in Zahlen',
                            subtitle: 'Unsere gemeinsame Zeit auf einen Blick',
                            stats: [
                                { label: 'Tage unterwegs', value: '7' },
                                { label: 'Gewandert', value: '85 km' },
                                { label: 'Pfadis', value: '38' },
                                { label: 'Stockbrote', value: '140+' }
                            ],
                            imageUrl: '',
                            brightness: 100
                        });
                    }

                    if (blogImages.length > 2) {
                        newSlides.push({
                            type: 'story',
                            layout: 'polaroid',
                            title: 'Lagerleben & Highlights',
                            description: 'Momente voller Freude, Gemeinschaft und Abenteuerlust.',
                            imageUrl: blogImages[2] || '',
                            brightness: 100
                        });
                    }

                    if (aiIncludeQuote) {
                        newSlides.push({
                            type: 'quote',
                            layout: 'quote_center',
                            title: '“Am Lagerfeuer werden aus Fremden die besten Freunde fürs Leben.”',
                            author: 'Stammes-Motto',
                            subtitle: 'Gemeinschaft, Abenteuer & Mut',
                            imageUrl: '',
                            brightness: 100
                        });
                    }
                } else if (effectiveSource === 'event' && eventData) {
                    newSlides.push({
                        type: 'cover',
                        layout: 'hero_full',
                        title: eventData.title || 'Kommende Aktion',
                        subtitle: eventData.date ? `Termin: ${eventData.date}` : 'Sei dabei und erlebe Gemeinschaft!',
                        tag: 'ANKÜNDIGUNG',
                        imageUrl: eventData.imageUrl || '',
                        brightness: 100
                    });

                    newSlides.push({
                        type: 'event',
                        layout: 'event_badge',
                        title: eventData.title || 'Aktions-Details',
                        subtitle: eventData.description || 'Alle wichtigen Infos zu unserer nächsten Stammesaktion.',
                        dateText: eventData.date || 'Demnächst',
                        locationText: eventData.location || 'Pfadiheim',
                        targetGroup: eventData.targetGroup || 'Alle Altersstufen',
                        imageUrl: eventData.imageUrl || '',
                        brightness: 100
                    });

                    if (aiIncludeStats) {
                        newSlides.push({
                            type: 'stats',
                            layout: 'stats_grid',
                            title: 'Was dich erwartet',
                            subtitle: 'Programm & Highlights auf einen Blick',
                            stats: [
                                { label: 'Tage Abenteuer', value: '1' },
                                { label: 'Geländespiele', value: '100%' },
                                { label: 'Verpflegung', value: 'Inkl.' },
                                { label: 'Neue Freunde', value: 'Viele' }
                            ],
                            imageUrl: '',
                            brightness: 100
                        });
                    }
                } else {
                    const topic = promptToSend.toLowerCase();
                    const genTitle = promptToSend.charAt(0).toUpperCase() + promptToSend.slice(1);

                    newSlides.push({
                        type: 'cover',
                        layout: 'hero_full',
                        title: genTitle.length > 35 ? genTitle.substring(0, 32) + '...' : genTitle,
                        subtitle: 'Einblicke, Erlebnisse & echte Pfadfindergemeinschaft',
                        tag: topic.includes('schnupper') ? 'SCHNUPPERTAG' : (topic.includes('termin') ? 'ANKÜNDIGUNG' : 'LAGERBERICHT'),
                        imageUrl: '',
                        brightness: 100
                    });

                    newSlides.push({
                        type: 'story',
                        layout: 'full_card',
                        title: 'Zusammenhalt & Schwarzzelte',
                        description: 'Gemeinsam bauten wir unsere Kohten und Jurten auf und richteten unser Lager ein.',
                        imageUrl: '',
                        brightness: 100
                    });

                    if (aiIncludeStats) {
                        newSlides.push({
                            type: 'stats',
                            layout: 'stats_grid',
                            title: 'Das Lager in Zahlen',
                            subtitle: 'Unsere gemeinsame Zeit auf einen Blick',
                            stats: [
                                { label: 'Tage im Zelt', value: '7' },
                                { label: 'Gewandert', value: '85 km' },
                                { label: 'Pfadis', value: '38' },
                                { label: 'Stockbrote', value: '140+' }
                            ],
                            imageUrl: '',
                            brightness: 100
                        });
                    }

                    newSlides.push({
                        type: 'story',
                        layout: 'polaroid',
                        title: 'Abenteuer & Orientierung',
                        description: 'Mit Karte und Kompass meisterten wir knifflige Geländespiele und Streifzüge.',
                        imageUrl: '',
                        brightness: 100
                    });

                    if (aiIncludeQuote) {
                        newSlides.push({
                            type: 'quote',
                            layout: 'quote_center',
                            title: '“Niemand weiß, wie weit seine Kräfte gehen, bis er sie versucht hat.”',
                            author: 'Robert Baden-Powell',
                            subtitle: 'Pfadfinder-Leitsatz für Mut & Tatkraft',
                            imageUrl: '',
                            brightness: 100
                        });
                    }
                }

                newSlides.push({
                    type: 'outro',
                    layout: 'outro_shield',
                    title: 'Lust auf dein eigenes Abenteuer?',
                    subtitle: 'Werde Teil unseres Stammes! Schreib uns eine Nachricht oder besuche unsere Website.',
                    website: websiteUrl,
                    handle: instagramHandle,
                    imageUrl: '',
                    brightness: 100
                });

                generatedData = {
                    themeId: aiThemeStyle === 'light' ? 'stamm_light' : 'stamm_custom',
                    slides: newSlides
                };
            }

            if (aiThemeStyle === 'light') {
                const lightMatch = themes.find(t => t.id === 'stamm_light') || themes.find(t => t.isLight);
                if (lightMatch) setThemeId(lightMatch.id);
            } else if (aiThemeStyle === 'dark') {
                const darkMatch = themes.find(t => t.id === 'stamm_custom') || themes.find(t => !t.isLight);
                if (darkMatch) setThemeId(darkMatch.id);
            } else if (generatedData.themeId && themes.some(t => t.id === generatedData.themeId)) {
                setThemeId(generatedData.themeId);
            }

            if (generatedData.slides && Array.isArray(generatedData.slides)) {
                setSlides(generatedData.slides);
                setActiveSlideIndex(0);
            }

            setIsAiModalOpen(false);
            setAiPrompt('');
        } catch (err) {
            console.error('Bob AI generation error:', err);
            alert('Fehler bei der KI-Generierung. Bitte versuche es erneut.');
        } finally {
            setIsAiGenerating(false);
            setAiStatusMessage('');
        }
    };

    // Quick Action: Generate Quote slide
    const handleQuickQuote = () => {
        const quotes = [
            { quote: '“Wer Pfadfinder ist, findet immer einen Weg durchs Dickicht.”', author: 'Stammesmotto', sub: 'Gemeinschaft & Verantwortung' },
            { quote: '“Ein Pfadfinder lächelt und pfeift in allen Schwierigkeiten.”', author: 'Robert Baden-Powell', sub: 'Gründer der Pfadfinderbewegung' },
            { quote: '“Verlasst die Welt ein bisschen besser, als ihr sie vorgefunden habt.”', author: 'Robert Baden-Powell', sub: 'Letzte Botschaft an alle Pfadfinder' },
            { quote: '“Am Lagerfeuer werden aus Fremden die besten Freunde fürs Leben.”', author: 'Sippenleiter', sub: 'Lagerleben & Zusammenhalt' },
            { quote: '“Draußen zuhause – gemeinsam die Natur entdecken und schützen.”', author: stammName, sub: 'Naturerlebnis & Nachhaltigkeit' }
        ];
        const randomQuote = quotes[Math.floor(Math.random() * quotes.length)];
        
        const quoteSlide = {
            type: 'quote',
            layout: 'quote_center',
            title: randomQuote.quote,
            author: randomQuote.author,
            subtitle: randomQuote.sub,
            imageUrl: '',
            brightness: 100
        };

        const hasOutro = slides[slides.length - 1]?.type === 'outro';
        if (hasOutro) {
            const updated = [...slides.slice(0, -1), quoteSlide, slides[slides.length - 1]];
            setSlides(updated);
            setActiveSlideIndex(updated.length - 2);
        } else {
            const updated = [...slides, quoteSlide];
            setSlides(updated);
            setActiveSlideIndex(updated.length - 1);
        }
    };

    // Quick Action: Generate Stats slide
    const handleQuickStats = () => {
        const statSlide = {
            type: 'stats',
            layout: 'stats_grid',
            title: 'Das Abenteuer in Zahlen',
            subtitle: 'Unsere Erlebnisse auf einen Blick',
            stats: [
                { label: 'Tage im Zelt', value: '7' },
                { label: 'Kilometer gewandert', value: '92 km' },
                { label: 'Pfadis', value: '42' },
                { label: 'Stockbrote gebacken', value: '150+' }
            ],
            imageUrl: '',
            brightness: 100
        };

        const hasOutro = slides[slides.length - 1]?.type === 'outro';
        if (hasOutro) {
            const updated = [...slides.slice(0, -1), statSlide, slides[slides.length - 1]];
            setSlides(updated);
            setActiveSlideIndex(updated.length - 2);
        } else {
            const updated = [...slides, statSlide];
            setSlides(updated);
            setActiveSlideIndex(updated.length - 1);
        }
    };

    const activeSlide = slides[activeSlideIndex] || slides[0];
    const detectedStammColor = themes[0]?.primaryHex || '#52B788';

    return (
        <div className="p-4 sm:p-6 md:p-8 space-y-8 animate-fade-in bg-surface text-surface-on">
            {/* Top Navigation & Action Header */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 pb-6 border-b border-outlineVariant/30">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate(-1)}
                        className="w-10 h-10 rounded-full hover:bg-surface-on/5 flex items-center justify-center transition-colors cursor-pointer text-surface-onVariant hover:text-surface-on"
                        title="Zurück zum Dashboard"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div>
                        <div className="flex items-center gap-2.5">
                            <span className="p-2 rounded-2xl bg-gradient-to-tr from-amber-500 to-pink-500 text-white flex items-center justify-center shadow-sm">
                                <InstagramIcon sx={{ fontSize: 22 }} />
                            </span>
                            <h1 className="text-title-large sm:text-headline-small font-bold tracking-tight text-surface-on">
                                Instagram Karussell Generator
                            </h1>
                            <span className="hidden sm:inline-flex px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-primary/10 text-primary border border-primary/20">
                                4:5 HD Studio
                            </span>
                        </div>
                        <p className="text-body-small text-surface-onVariant mt-1">
                            Erstelle professionelle, druckreife Instagram-Beiträge & Karussells im authentischen Stammes-Design.
                        </p>
                    </div>
                </div>

                {/* Header Action Buttons */}
                <div className="flex flex-wrap items-center gap-2.5">
                    {isBobEnabled && (
                        <button
                            onClick={() => {
                                setAiSourceMode('topic');
                                setIsAiModalOpen(true);
                            }}
                            className="flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-gradient-to-r from-primary/15 to-secondary/15 hover:from-primary/25 hover:to-secondary/25 text-primary font-bold text-xs border border-primary/30 transition-all shadow-sm cursor-pointer"
                        >
                            <Bot className="w-4 h-4 text-primary" />
                            <span>Bob Assistent</span>
                        </button>
                    )}

                    <button
                        onClick={handleCopyCaption}
                        className="flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-surface-containerHigh hover:bg-surface-containerHighest text-surface-on font-bold text-xs border border-outlineVariant/30 transition-all shadow-sm cursor-pointer"
                    >
                        {copiedCaption ? <Check className="w-4 h-4 text-primary" /> : <Copy className="w-4 h-4 text-surface-onVariant" />}
                        <span>{copiedCaption ? 'Caption kopiert' : 'Caption kopieren'}</span>
                    </button>

                    <button
                        onClick={handleDownloadAllZip}
                        disabled={isExportingZip}
                        className="md3-btn-filled flex items-center gap-2 px-5 h-11 text-xs font-bold shadow-md cursor-pointer disabled:opacity-50"
                    >
                        {isExportingZip ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                        <span>{isExportingZip ? 'Erstelle ZIP...' : `Alle ${slides.length} Folien (ZIP)`}</span>
                    </button>
                </div>
            </div>

            {/* Quick Import & Co-Pilot Bar */}
            <div className="p-4 rounded-3xl bg-surface-containerLow border border-outlineVariant/30 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 shadow-sm">
                <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-2xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                        {isBobEnabled ? <Bot className="w-5 h-5" /> : <Layers className="w-5 h-5" />}
                    </div>
                    <div>
                        <h4 className="text-title-small font-bold text-surface-on">
                            {isBobEnabled ? 'Bob Co-Pilot & Import' : 'Inhalte importieren'}
                        </h4>
                        <p className="text-body-small text-surface-onVariant">
                            {isBobEnabled ? 'Lass Bob Folien befüllen oder importiere Inhalte aus Blog & Events.' : 'Übernehme Inhalte direkt aus Blogbeiträgen oder anstehenden Events.'}
                        </p>
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-2.5 w-full md:w-auto">
                    {/* Quick Folien Actions */}
                    <button
                        onClick={handleQuickStats}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-tertiary/10 hover:bg-tertiary/20 text-tertiary text-xs font-bold transition-all cursor-pointer border border-tertiary/20"
                        title="Zahlen-Folie hinzufügen"
                    >
                        <BarChart2 className="w-3.5 h-3.5" />
                        <span>Zahlen-Folie</span>
                    </button>

                    <button
                        onClick={handleQuickQuote}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-secondary/10 hover:bg-secondary/20 text-secondary text-xs font-bold transition-all cursor-pointer border border-secondary/20"
                        title="Zitat-Folie hinzufügen"
                    >
                        <Quote className="w-3.5 h-3.5" />
                        <span>Zitat-Folie</span>
                    </button>

                    {/* Blog Import */}
                    <div className="flex items-center gap-1.5 bg-surface-containerLowest border border-outlineVariant/40 rounded-xl px-2.5 py-1.5 text-xs">
                        <Newspaper className="w-3.5 h-3.5 text-primary" />
                        <span className="text-surface-onVariant font-semibold">Blog:</span>
                        <select
                            value={selectedBlogId}
                            onChange={(e) => {
                                handleSelectBlog(e.target.value);
                            }}
                            disabled={loadingImports}
                            className="bg-transparent text-primary font-bold focus:outline-none cursor-pointer max-w-[130px] truncate"
                        >
                            <option value="">-- Wählen --</option>
                            {blogPosts.map(p => (
                                <option key={p._id} value={p._id}>{p.title}</option>
                            ))}
                        </select>

                        {isBobEnabled && selectedBlogId && (
                            <button
                                type="button"
                                onClick={() => handleRunBobAiGenerator('blog', selectedBlogId)}
                                className="px-2 py-0.5 rounded-lg bg-primary text-primary-on text-[11px] font-bold hover:opacity-90 flex items-center gap-1 cursor-pointer"
                                title="Bob erstellt ein komplettes Karussell aus diesem Blogbeitrag"
                            >
                                <Sparkles className="w-3 h-3" />
                                <span>Bob</span>
                            </button>
                        )}
                    </div>

                    {/* Event Import */}
                    <div className="flex items-center gap-1.5 bg-surface-containerLowest border border-outlineVariant/40 rounded-xl px-2.5 py-1.5 text-xs">
                        <Calendar className="w-3.5 h-3.5 text-secondary" />
                        <span className="text-surface-onVariant font-semibold">Event:</span>
                        <select
                            value={selectedEventId}
                            onChange={(e) => {
                                handleSelectEvent(e.target.value);
                            }}
                            disabled={loadingImports}
                            className="bg-transparent text-primary font-bold focus:outline-none cursor-pointer max-w-[130px] truncate"
                        >
                            <option value="">-- Wählen --</option>
                            {events.map(e => (
                                <option key={e._id} value={e._id}>{e.title}</option>
                            ))}
                        </select>

                        {isBobEnabled && selectedEventId && (
                            <button
                                type="button"
                                onClick={() => handleRunBobAiGenerator('event', selectedEventId)}
                                className="px-2 py-0.5 rounded-lg bg-secondary text-white text-[11px] font-bold hover:opacity-90 flex items-center gap-1 cursor-pointer"
                                title="Bob erstellt eine Ankündigung aus diesem Event"
                            >
                                <Sparkles className="w-3 h-3" />
                                <span>Bob</span>
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Main Content Layout Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                {/* Left Side: Editor & Controls (7 cols) */}
                <div className="lg:col-span-7 space-y-6">
                    {/* Navigation Tabs */}
                    <div className="flex items-center gap-2 p-1.5 rounded-2xl bg-surface-containerLow border border-outlineVariant/20">
                        <button
                            onClick={() => setActiveTab('slides')}
                            className={`flex-1 py-2 px-3 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 cursor-pointer ${
                                activeTab === 'slides'
                                    ? 'bg-surface-containerLowest text-primary shadow-sm'
                                    : 'text-surface-onVariant hover:text-surface-on'
                            }`}
                        >
                            <Layers className="w-4 h-4" />
                            <span>Folien ({slides.length})</span>
                        </button>

                        <button
                            onClick={() => setActiveTab('branding')}
                            className={`flex-1 py-2 px-3 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 cursor-pointer ${
                                activeTab === 'branding'
                                    ? 'bg-surface-containerLowest text-primary shadow-sm'
                                    : 'text-surface-onVariant hover:text-surface-on'
                            }`}
                        >
                            <Shield className="w-4 h-4" />
                            <span>Logo & Farben</span>
                        </button>

                        <button
                            onClick={() => setActiveTab('caption')}
                            className={`flex-1 py-2 px-3 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 cursor-pointer ${
                                activeTab === 'caption'
                                    ? 'bg-surface-containerLowest text-primary shadow-sm'
                                    : 'text-surface-onVariant hover:text-surface-on'
                            }`}
                        >
                            <Type className="w-4 h-4" />
                            <span>Caption & Hashtags</span>
                        </button>
                    </div>

                    {/* TAB: BRANDING & THEME */}
                    {activeTab === 'branding' && (
                        <div className="space-y-6 animate-fade-in">
                            {/* Theme Selector with Stamm-Design */}
                            <div className="md3-card-elevated p-6 rounded-3xl border border-outlineVariant/30 bg-surface-containerLow">
                                <div className="flex items-center justify-between mb-2">
                                    <h3 className="text-title-medium font-bold text-surface-on flex items-center gap-2">
                                        <Palette className="w-4 h-4 text-primary" />
                                        <span>Farbthemen & Stammes-Stil</span>
                                    </h3>
                                    <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-primary/10 text-primary border border-primary/20">
                                        Stamm-Farben aktiv
                                    </span>
                                </div>
                                <p className="text-body-small text-surface-onVariant mb-4">
                                    Wähle ein helles Design (perfekt für sonnige Sommerlager- und Outdoor-Fotos) oder ein kontrastreiches dunkles Design.
                                </p>

                                {/* Theme Filter Tabs */}
                                <div className="flex items-center gap-1.5 p-1 rounded-xl bg-surface-containerLowest border border-outlineVariant/30 mb-4 w-fit">
                                    <button
                                        type="button"
                                        onClick={() => setThemeFilter('all')}
                                        className={`px-3 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                                            themeFilter === 'all'
                                                ? 'bg-primary text-primary-on shadow-xs'
                                                : 'text-surface-onVariant hover:text-surface-on'
                                        }`}
                                    >
                                        Alle ({themes.length})
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setThemeFilter('light')}
                                        className={`px-3 py-1 rounded-lg text-xs font-bold transition-all flex items-center gap-1 cursor-pointer ${
                                            themeFilter === 'light'
                                                ? 'bg-amber-500 text-white shadow-xs'
                                                : 'text-surface-onVariant hover:text-surface-on'
                                        }`}
                                    >
                                        <span>☀️ Hell ({themes.filter(t => t.isLight).length})</span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setThemeFilter('dark')}
                                        className={`px-3 py-1 rounded-lg text-xs font-bold transition-all flex items-center gap-1 cursor-pointer ${
                                            themeFilter === 'dark'
                                                ? 'bg-slate-800 text-white shadow-xs'
                                                : 'text-surface-onVariant hover:text-surface-on'
                                        }`}
                                    >
                                        <span>🌙 Dunkel ({themes.filter(t => !t.isLight).length})</span>
                                    </button>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                                    {themes.filter(t => {
                                        if (themeFilter === 'light') return t.isLight === true;
                                        if (themeFilter === 'dark') return t.isLight === false;
                                        return true;
                                    }).map(t => (
                                        <button
                                            key={t.id}
                                            onClick={() => setThemeId(t.id)}
                                            className={`p-3.5 rounded-2xl border text-left transition-all flex items-center gap-3 cursor-pointer ${
                                                themeId === t.id
                                                    ? 'border-primary bg-primary/10 shadow-sm ring-1 ring-primary'
                                                    : 'border-outlineVariant/30 bg-surface-containerLowest hover:bg-surface-container'
                                            } ${t.isStammBrand ? 'border-primary/50' : ''}`}
                                        >
                                            <div className="w-9 h-9 rounded-2xl border border-white/20 shrink-0 flex items-center justify-center shadow-inner" style={{ backgroundColor: t.accentColor }}>
                                                <div className="w-4 h-4 rounded-full" style={{ backgroundColor: t.bgColor }} />
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-1.5 flex-wrap">
                                                    <h4 className="text-xs font-bold text-surface-on truncate">{t.name}</h4>
                                                    <span className={`px-1.5 py-0.2 rounded text-[9px] font-bold ${
                                                        t.isLight ? 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-500/30' : 'bg-slate-700/30 text-slate-300 border border-slate-600/30'
                                                    }`}>
                                                        {t.isLight ? '☀️ HELL' : '🌙 DUNKEL'}
                                                    </span>
                                                    {t.isStammBrand && (
                                                        <span className="px-1.5 py-0.2 rounded text-[9px] font-bold bg-primary text-primary-on">
                                                            STAMM
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-[11px] text-surface-onVariant truncate">{t.subtitle}</p>
                                            </div>
                                            {themeId === t.id && <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />}
                                        </button>
                                    ))}
                                </div>

                                {/* Custom Stamm Primary Color Adjuster */}
                                <div className="p-4 rounded-2xl bg-surface-containerLowest border border-outlineVariant/30 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-xl border border-outlineVariant/40 shadow-xs shrink-0" style={{ backgroundColor: detectedStammColor }} />
                                        <div>
                                            <span className="text-xs font-bold text-surface-on block">Erkannte Stammes-Hauptfarbe</span>
                                            <span className="text-[11px] font-mono text-surface-onVariant">{detectedStammColor}</span>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <label className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-surface-containerHigh hover:bg-surface-containerHighest border border-outlineVariant/40 text-xs font-semibold cursor-pointer transition-colors text-surface-on">
                                            <Pipette className="w-3.5 h-3.5 text-primary" />
                                            <span>Farbe anpassen</span>
                                            <input
                                                type="color"
                                                value={detectedStammColor}
                                                onChange={(e) => {
                                                    setCustomStammColor(e.target.value);
                                                    setThemeId('stamm_custom');
                                                }}
                                                className="opacity-0 w-0 h-0 absolute pointer-events-none"
                                            />
                                        </label>
                                        {customStammColor && (
                                            <button
                                                type="button"
                                                onClick={() => setCustomStammColor('')}
                                                className="text-[11px] text-surface-onVariant hover:text-surface-on underline cursor-pointer"
                                            >
                                                Zurücksetzen
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Logo Customizer */}
                            <div className="md3-card-elevated p-6 rounded-3xl border border-outlineVariant/30 bg-surface-containerLow space-y-5">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h3 className="text-title-medium font-bold text-surface-on flex items-center gap-2">
                                            <Shield className="w-4 h-4 text-primary" />
                                            <span>Stammes-Logo</span>
                                        </h3>
                                        <p className="text-body-small text-surface-onVariant mt-0.5">
                                            Wird automatisch direkt aus dem Browser-Tab / den Stammes-Einstellungen übernommen.
                                        </p>
                                    </div>
                                    {logoUrl && (
                                        <div className="w-12 h-12 rounded-2xl bg-white border border-outlineVariant/40 p-1 flex items-center justify-center shadow-xs">
                                            <img src={logoUrl} alt="Logo" className="max-w-full max-h-full object-contain" />
                                        </div>
                                    )}
                                </div>

                                {/* Logo Upload & URL & Auto Tab Reload */}
                                <div>
                                    <div className="flex items-center justify-between mb-1.5">
                                        <label className="block text-label-small font-bold text-surface-onVariant">
                                            Logo-Quelle (URL oder Bild hochladen)
                                        </label>
                                        <button
                                            type="button"
                                            onClick={handleReloadTabFavicon}
                                            className="text-[11px] text-primary font-bold hover:underline flex items-center gap-1 cursor-pointer"
                                            title="Liest das Favicon direkt aus dem aktuellen Browser-Tab"
                                        >
                                            <RefreshCw className="w-3 h-3" />
                                            <span>Aus Browser-Tab neu laden</span>
                                        </button>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <input
                                            type="text"
                                            value={logoUrl}
                                            onChange={(e) => setLogoUrl(e.target.value)}
                                            placeholder="https://... (automatisch aus Browser-Tab)"
                                            className="flex-1 bg-surface-containerLowest border border-outlineVariant/40 rounded-2xl px-3.5 py-2.5 text-xs text-surface-on font-mono focus:outline-none focus:border-primary truncate"
                                        />
                                        <label className="md3-btn-tonal flex items-center gap-1.5 px-4 h-10 rounded-2xl text-xs font-bold cursor-pointer shrink-0">
                                            <ImageIcon className="w-4 h-4 text-primary" />
                                            <span>{isUploadingLogo ? 'Lädt...' : 'Logo hochladen'}</span>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={handleLogoUpload}
                                                disabled={isUploadingLogo}
                                                className="hidden"
                                            />
                                        </label>
                                    </div>
                                </div>
                            </div>

                            {/* Stamm Branding Customizer */}
                            <div className="md3-card-elevated p-6 rounded-3xl border border-outlineVariant/30 bg-surface-containerLow space-y-4">
                                <h3 className="text-title-medium font-bold text-surface-on flex items-center gap-2">
                                    <Settings2 className="w-4 h-4 text-primary" />
                                    <span>Stammes-Texte & Handles</span>
                                </h3>
                                <p className="text-body-small text-surface-onVariant">
                                    Diese Angaben erscheinen im Header jeder Folie, auf dem Deckblatt und auf der Schluss-Kontaktfolie.
                                </p>

                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                    <div>
                                        <label className="block text-label-small font-bold text-surface-onVariant mb-1">Stamm Name</label>
                                        <input
                                            type="text"
                                            value={stammName}
                                            onChange={(e) => setStammName(e.target.value)}
                                            className="w-full bg-surface-containerLowest border border-outlineVariant/40 rounded-xl px-3 py-2 text-xs text-surface-on font-semibold focus:outline-none focus:border-primary"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-label-small font-bold text-surface-onVariant mb-1">Website URL</label>
                                        <input
                                            type="text"
                                            value={websiteUrl}
                                            onChange={(e) => setWebsiteUrl(e.target.value)}
                                            className="w-full bg-surface-containerLowest border border-outlineVariant/40 rounded-xl px-3 py-2 text-xs text-surface-on font-semibold focus:outline-none focus:border-primary"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-label-small font-bold text-surface-onVariant mb-1">Instagram Handle</label>
                                        <input
                                            type="text"
                                            value={instagramHandle}
                                            onChange={(e) => setInstagramHandle(e.target.value)}
                                            className="w-full bg-surface-containerLowest border border-outlineVariant/40 rounded-xl px-3 py-2 text-xs text-surface-on font-semibold focus:outline-none focus:border-primary"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* TAB: CAPTION & HASHTAGS */}
                    {activeTab === 'caption' && (
                        <div className="md3-card-elevated p-6 rounded-3xl border border-outlineVariant/30 bg-surface-containerLow space-y-4 animate-fade-in">
                            <div className="flex items-center justify-between">
                                <h3 className="text-title-medium font-bold text-surface-on flex items-center gap-2">
                                    <Type className="w-4 h-4 text-primary" />
                                    <span>Instagram Caption & Hashtags</span>
                                </h3>
                                <button
                                    onClick={handleCopyCaption}
                                    className="md3-btn-tonal flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold cursor-pointer"
                                >
                                    {copiedCaption ? <Check className="w-3.5 h-3.5 text-primary" /> : <Copy className="w-3.5 h-3.5" />}
                                    <span>{copiedCaption ? 'Kopiert!' : 'Kopieren'}</span>
                                </button>
                            </div>

                            <p className="text-body-small text-surface-onVariant">
                                Der folgende Text wird automatisch aus deinen Folien generiert und kann direkt in Instagram eingefügt werden:
                            </p>

                            <textarea
                                rows={12}
                                value={generateCaption()}
                                readOnly
                                className="w-full font-mono text-xs bg-surface-containerLowest border border-outlineVariant/40 rounded-2xl p-4 text-surface-on focus:outline-none leading-relaxed"
                            />
                        </div>
                    )}

                    {/* TAB: SLIDES MANAGER & ACTIVE SLIDE EDITOR */}
                    {activeTab === 'slides' && (
                        <div className="space-y-6">
                            {/* Slide Strip & Reordering */}
                            <div className="md3-card-elevated p-5 rounded-3xl border border-outlineVariant/30 bg-surface-containerLow shadow-sm">
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-2">
                                        <Layers className="w-4 h-4 text-primary" />
                                        <h3 className="text-title-small font-bold text-surface-on">
                                            Folien-Reihenfolge ({slides.length} Folien)
                                        </h3>
                                    </div>

                                    {/* Add Slide Dropdown Buttons */}
                                    <div className="flex items-center gap-1.5">
                                        <button
                                            onClick={() => handleAddSlide('story')}
                                            className="flex items-center gap-1 px-2.5 py-1.5 rounded-xl bg-primary/10 hover:bg-primary/20 text-primary text-xs font-bold transition-colors cursor-pointer"
                                            title="Foto-Folie hinzufügen"
                                        >
                                            <Plus className="w-3.5 h-3.5" />
                                            <span>Foto</span>
                                        </button>
                                        <button
                                            onClick={() => handleAddSlide('quote')}
                                            className="flex items-center gap-1 px-2.5 py-1.5 rounded-xl bg-secondary/10 hover:bg-secondary/20 text-secondary text-xs font-bold transition-colors cursor-pointer"
                                            title="Zitat-Folie hinzufügen"
                                        >
                                            <Quote className="w-3.5 h-3.5" />
                                            <span>Zitat</span>
                                        </button>
                                        <button
                                            onClick={() => handleAddSlide('stats')}
                                            className="flex items-center gap-1 px-2.5 py-1.5 rounded-xl bg-tertiary/10 hover:bg-tertiary/20 text-tertiary text-xs font-bold transition-colors cursor-pointer"
                                            title="Fakten/Zahlen-Folie hinzufügen"
                                        >
                                            <BarChart2 className="w-3.5 h-3.5" />
                                            <span>Fakten</span>
                                        </button>
                                        <button
                                            onClick={() => handleAddSlide('event')}
                                            className="flex items-center gap-1 px-2.5 py-1.5 rounded-xl bg-amber-500/10 hover:bg-amber-500/20 text-amber-600 dark:text-amber-400 text-xs font-bold transition-colors cursor-pointer"
                                            title="Terminankündigungs-Folie hinzufügen"
                                        >
                                            <Calendar className="w-3.5 h-3.5" />
                                            <span>Termin</span>
                                        </button>
                                    </div>
                                </div>

                                {/* Slides Horizontal Strip */}
                                <div className="flex items-center gap-3 overflow-x-auto pb-2 custom-scrollbar pt-1">
                                    {slides.map((s, idx) => (
                                        <div
                                            key={idx}
                                            onClick={() => setActiveSlideIndex(idx)}
                                            className={`relative shrink-0 w-28 h-36 rounded-2xl border-2 cursor-pointer transition-all overflow-hidden flex flex-col justify-between p-2.5 ${
                                                activeSlideIndex === idx
                                                    ? 'border-primary ring-2 ring-primary/25 bg-surface-containerLowest shadow-md scale-[1.02]'
                                                    : 'border-outlineVariant/30 bg-surface-containerLowest hover:border-outlineVariant/60 opacity-80 hover:opacity-100'
                                            }`}
                                        >
                                            {s.imageUrl && (
                                                <img
                                                    src={s.imageUrl}
                                                    alt=""
                                                    className="absolute inset-0 w-full h-full object-cover opacity-35 pointer-events-none"
                                                />
                                            )}
                                            <div className="relative z-10 flex items-center justify-between">
                                                <span className="px-2 py-0.5 rounded-md bg-black/60 text-[10px] font-extrabold text-white">
                                                    #{idx + 1}
                                                </span>
                                                <div className="flex items-center gap-1">
                                                    {s.type !== 'cover' && s.type !== 'outro' && (
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleRemoveSlide(idx);
                                                            }}
                                                            className="p-1 rounded-md hover:bg-error/20 text-surface-onVariant hover:text-error transition-colors"
                                                            title="Folie entfernen"
                                                        >
                                                            <Trash2 className="w-3 h-3" />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="relative z-10">
                                                <span className="text-[9px] uppercase tracking-wider font-extrabold text-primary block truncate">
                                                    {s.type === 'cover' && 'Titel'}
                                                    {s.type === 'story' && 'Story'}
                                                    {s.type === 'quote' && 'Zitat'}
                                                    {s.type === 'stats' && 'Fakten'}
                                                    {s.type === 'event' && 'Termin'}
                                                    {s.type === 'outro' && 'Schluss'}
                                                </span>
                                                <p className="text-[11px] font-bold text-surface-on truncate">
                                                    {s.title || '(Kein Titel)'}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Active Slide Form Editor */}
                            <div className="md3-card-elevated p-6 rounded-3xl border border-outlineVariant/30 bg-surface-containerLow shadow-sm space-y-5 min-h-[540px]">
                                <div className="flex items-center justify-between pb-4 border-b border-outlineVariant/20">
                                    <div className="flex items-center gap-2.5">
                                        <span className="px-3 py-1 rounded-xl bg-primary text-primary-on text-xs font-black uppercase shadow-2xs">
                                            Folie {activeSlideIndex + 1}: {activeSlide.type}
                                        </span>
                                        <span className="text-xs text-surface-onVariant font-medium">
                                            {activeSlide.type === 'cover' && 'Titel- & Deckblatt'}
                                            {activeSlide.type === 'story' && 'Inhalts- & Foto-Folie'}
                                            {activeSlide.type === 'quote' && 'Zitat & Pfadfinder-Stimme'}
                                            {activeSlide.type === 'stats' && 'Lager-Zahlen & Fakten-Raster'}
                                            {activeSlide.type === 'event' && 'Termin- & Aktionsankündigung'}
                                            {activeSlide.type === 'outro' && 'Schluss- & Kontaktfolie'}
                                        </span>
                                    </div>

                                    {/* Action Bar for Active Slide */}
                                    <div className="flex items-center gap-1">
                                        <button
                                            onClick={() => handleMoveSlide(activeSlideIndex, activeSlideIndex - 1)}
                                            disabled={activeSlideIndex === 0}
                                            className="p-2 rounded-xl bg-surface-containerLowest hover:bg-surface-containerHighest text-surface-onVariant hover:text-surface-on disabled:opacity-30 transition-colors cursor-pointer"
                                            title="Folie nach links schieben"
                                        >
                                            <MoveLeft className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={() => handleMoveSlide(activeSlideIndex, activeSlideIndex + 1)}
                                            disabled={activeSlideIndex === slides.length - 1}
                                            className="p-2 rounded-xl bg-surface-containerLowest hover:bg-surface-containerHighest text-surface-onVariant hover:text-surface-on disabled:opacity-30 transition-colors cursor-pointer"
                                            title="Folie nach rechts schieben"
                                        >
                                            <MoveRight className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={() => handleDuplicateSlide(activeSlideIndex)}
                                            className="p-2 rounded-xl bg-surface-containerLowest hover:bg-surface-containerHighest text-surface-onVariant hover:text-surface-on transition-colors cursor-pointer"
                                            title="Folie duplizieren"
                                        >
                                            <CopyPlus className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>

                                {/* Slide Layout Selector (Clean typography) */}
                                {activeSlide.type === 'story' && (
                                    <div className="space-y-1.5">
                                        <label className="block text-label-small font-bold text-surface-onVariant">Layout-Stil</label>
                                        <div className="grid grid-cols-2 gap-2">
                                            <button
                                                type="button"
                                                onClick={() => updateActiveSlide('layout', 'full_card')}
                                                className={`py-2 px-3 rounded-xl border text-xs font-bold transition-all cursor-pointer ${
                                                    activeSlide.layout === 'full_card'
                                                        ? 'bg-primary/10 border-primary text-primary'
                                                        : 'bg-surface-containerLowest border-outlineVariant/30 text-surface-onVariant'
                                                }`}
                                            >
                                                Karte (Unten)
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => updateActiveSlide('layout', 'polaroid')}
                                                className={`py-2 px-3 rounded-xl border text-xs font-bold transition-all cursor-pointer ${
                                                    activeSlide.layout === 'polaroid'
                                                        ? 'bg-primary/10 border-primary text-primary'
                                                        : 'bg-surface-containerLowest border-outlineVariant/30 text-surface-onVariant'
                                                }`}
                                            >
                                                Polaroid
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* Slide Title */}
                                <div>
                                    <label className="block text-label-small font-bold text-surface-onVariant mb-1.5">
                                        {activeSlide.type === 'cover' ? 'Haupttitel des Beitrags' : (activeSlide.type === 'quote' ? 'Zitat-Text' : (activeSlide.type === 'outro' ? 'Schluss-Überschrift & Aufruf' : 'Überschrift / Bildtitel'))}
                                    </label>
                                    <input
                                        type="text"
                                        value={activeSlide.title || ''}
                                        onChange={(e) => updateActiveSlide('title', e.target.value)}
                                        placeholder={activeSlide.type === 'cover' ? 'z. B. Sommerlager 2026' : (activeSlide.type === 'outro' ? 'z. B. Jetzt anmelden & mitmachen!' : 'z. B. Baden im Freibad')}
                                        className="w-full bg-surface-containerLowest border border-outlineVariant/40 rounded-2xl px-4 py-3 text-sm text-surface-on font-semibold focus:outline-none focus:border-primary"
                                    />
                                </div>

                                {/* Author field for Quote */}
                                {activeSlide.type === 'quote' && (
                                    <div>
                                        <label className="block text-label-small font-bold text-surface-onVariant mb-1.5">
                                            Autor / Sprecher / Quelle
                                        </label>
                                        <input
                                            type="text"
                                            value={activeSlide.author || ''}
                                            onChange={(e) => updateActiveSlide('author', e.target.value)}
                                            placeholder="z. B. Robert Baden-Powell oder Sippenleiter Max"
                                            className="w-full bg-surface-containerLowest border border-outlineVariant/40 rounded-2xl px-4 py-2.5 text-sm text-surface-on font-semibold focus:outline-none focus:border-primary"
                                        />
                                    </div>
                                )}

                                {/* Subtitle / Description / Story text */}
                                {activeSlide.type !== 'stats' && (
                                    <div>
                                        <label className="block text-label-small font-bold text-surface-onVariant mb-1.5">
                                            {activeSlide.type === 'cover' ? 'Untertitel / Datum' : (activeSlide.type === 'quote' ? 'Zusatz / Kontext' : (activeSlide.type === 'outro' ? 'Kurze Beschreibung / Info' : 'Kurze Beschreibung / Story'))}
                                        </label>
                                        <textarea
                                            rows={3}
                                            value={activeSlide.type === 'story' ? (activeSlide.description || '') : (activeSlide.subtitle || '')}
                                            onChange={(e) => updateActiveSlide(activeSlide.type === 'story' ? 'description' : 'subtitle', e.target.value)}
                                            placeholder={activeSlide.type === 'story' ? 'Hier waren wir nach unserer anstrengenden Wanderung baden im See...' : (activeSlide.type === 'outro' ? 'Alle Anmeldeunterlagen & Infos findest du auf unserer Website.' : '7 Tage voller Abenteuer im Schwarzwald')}
                                            className="w-full bg-surface-containerLowest border border-outlineVariant/40 rounded-2xl px-4 py-3 text-sm text-surface-on focus:outline-none focus:border-primary resize-none"
                                        />
                                    </div>
                                )}

                                {/* Tag Pill for Cover & Outro */}
                                {(activeSlide.type === 'cover' || activeSlide.type === 'outro') && (
                                    <div>
                                        <label className="block text-label-small font-bold text-surface-onVariant mb-1.5">
                                            Kategorie / Tag-Pille
                                        </label>
                                        <input
                                            type="text"
                                            value={activeSlide.tag || ''}
                                            onChange={(e) => updateActiveSlide('tag', e.target.value)}
                                            placeholder={activeSlide.type === 'outro' ? 'z. B. SCHLUSS & KONTAKT, MITMACHEN' : 'z. B. LAGERBERICHT, HIGHLIGHT, AKTION, HAJIK'}
                                            className="w-full bg-surface-containerLowest border border-outlineVariant/40 rounded-2xl px-4 py-2.5 text-sm text-surface-on font-semibold focus:outline-none focus:border-primary"
                                        />
                                    </div>
                                )}

                                {/* Stats Grid Editor */}
                                {activeSlide.type === 'stats' && (
                                    <div className="space-y-3">
                                        <div className="flex items-center justify-between">
                                            <label className="block text-label-small font-bold text-surface-onVariant">
                                                4 Fakten / Zahlen definieren
                                            </label>
                                            {isBobEnabled && (
                                                <button
                                                    type="button"
                                                    onClick={handleQuickStats}
                                                    className="text-[11px] text-primary font-bold hover:underline flex items-center gap-1 cursor-pointer"
                                                >
                                                    <Bot className="w-3 h-3" />
                                                    <span>Mit Bob neu ausfüllen</span>
                                                </button>
                                            )}
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            {[0, 1, 2, 3].map((statIdx) => {
                                                const st = (activeSlide.stats && activeSlide.stats[statIdx]) || { label: '', value: '' };
                                                return (
                                                    <div key={statIdx} className="p-3.5 rounded-2xl bg-surface-containerLowest border border-outlineVariant/30 space-y-2">
                                                        <input
                                                            type="text"
                                                            value={st.value || ''}
                                                            onChange={(e) => updateStatItem(statIdx, 'value', e.target.value)}
                                                            placeholder="Wert (z. B. 7 oder 85 km)"
                                                            className="w-full bg-surface-container border border-outlineVariant/30 rounded-lg px-2.5 py-1.5 text-xs font-bold text-primary"
                                                        />
                                                        <input
                                                            type="text"
                                                            value={st.label || ''}
                                                            onChange={(e) => updateStatItem(statIdx, 'label', e.target.value)}
                                                            placeholder="Beschreibung (z. B. Tage im Zelt)"
                                                            className="w-full bg-surface-container border border-outlineVariant/30 rounded-lg px-2.5 py-1 text-xs text-surface-on"
                                                        />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {/* Event Announcement Editor */}
                                {activeSlide.type === 'event' && (
                                    <div className="space-y-3 p-4 rounded-2xl bg-surface-containerLowest border border-outlineVariant/30">
                                        <label className="block text-label-small font-bold text-surface-onVariant">
                                            Termin-Details
                                        </label>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            <div>
                                                <label className="block text-[11px] font-bold text-surface-onVariant mb-1">Datum / Zeitraum</label>
                                                <input
                                                    type="text"
                                                    value={activeSlide.dateText || ''}
                                                    onChange={(e) => updateActiveSlide('dateText', e.target.value)}
                                                    placeholder="z. B. 15. - 22. August 2026"
                                                    className="w-full bg-surface-container border border-outlineVariant/30 rounded-xl px-3 py-1.5 text-xs text-surface-on font-semibold"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-[11px] font-bold text-surface-onVariant mb-1">Veranstaltungsort</label>
                                                <input
                                                    type="text"
                                                    value={activeSlide.locationText || ''}
                                                    onChange={(e) => updateActiveSlide('locationText', e.target.value)}
                                                    placeholder="z. B. Zeltplatz im Schwarzwald"
                                                    className="w-full bg-surface-container border border-outlineVariant/30 rounded-xl px-3 py-1.5 text-xs text-surface-on font-semibold"
                                                />
                                            </div>
                                            <div className="sm:col-span-2">
                                                <label className="block text-[11px] font-bold text-surface-onVariant mb-1">Zielgruppe / Stufe</label>
                                                <input
                                                    type="text"
                                                    value={activeSlide.targetGroup || ''}
                                                    onChange={(e) => updateActiveSlide('targetGroup', e.target.value)}
                                                    placeholder="z. B. Wölflinge, Pfadfinder & Rover (7-18 Jahre)"
                                                    className="w-full bg-surface-container border border-outlineVariant/30 rounded-xl px-3 py-1.5 text-xs text-surface-on font-semibold"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Outro Contact Details */}
                                {activeSlide.type === 'outro' && (
                                    <div className="space-y-3 p-4 rounded-2xl bg-surface-containerLowest border border-outlineVariant/30">
                                        <label className="block text-label-small font-bold text-surface-onVariant">
                                            Kontakt- & Social-Media-Links
                                        </label>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            <div>
                                                <label className="block text-[11px] font-bold text-surface-onVariant mb-1">Website URL</label>
                                                <input
                                                    type="text"
                                                    value={activeSlide.website !== undefined ? activeSlide.website : websiteUrl}
                                                    onChange={(e) => updateActiveSlide('website', e.target.value)}
                                                    placeholder="z. B. pfadfinder-wutoeschingen.de"
                                                    className="w-full bg-surface-container border border-outlineVariant/30 rounded-xl px-3 py-1.5 text-xs text-surface-on font-semibold"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-[11px] font-bold text-surface-onVariant mb-1">Instagram Handle</label>
                                                <input
                                                    type="text"
                                                    value={activeSlide.handle !== undefined ? activeSlide.handle : instagramHandle}
                                                    onChange={(e) => updateActiveSlide('handle', e.target.value)}
                                                    placeholder="z. B. @pfadfinder_wutoeschingen"
                                                    className="w-full bg-surface-container border border-outlineVariant/30 rounded-xl px-3 py-1.5 text-xs text-surface-on font-semibold"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Image Uploader & Filter Adjustments */}
                                <div className="space-y-3 pt-2">
                                    <label className="block text-label-small font-bold text-surface-onVariant">
                                        {activeSlide.type === 'outro' ? 'Hintergrundbild (optional)' : 'Foto der Folie'}
                                    </label>
                                    <div className="flex items-center gap-3">
                                        <input
                                            type="text"
                                            value={activeSlide.imageUrl || ''}
                                            onChange={(e) => updateActiveSlide('imageUrl', e.target.value)}
                                            placeholder="https://... oder Bild hochladen"
                                            className="flex-1 bg-surface-containerLowest border border-outlineVariant/40 rounded-2xl px-3.5 py-2.5 text-xs text-surface-on font-mono focus:outline-none focus:border-primary truncate"
                                        />
                                        <label className="md3-btn-tonal flex items-center gap-1.5 px-4 h-10 rounded-2xl text-xs font-bold cursor-pointer shrink-0">
                                            <ImageIcon className="w-4 h-4 text-primary" />
                                            <span>{isUploading ? 'Lädt...' : 'Hochladen'}</span>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={handleSlideImageUpload}
                                                disabled={isUploading}
                                                className="hidden"
                                            />
                                        </label>
                                    </div>

                                    {/* Brightness Adjustment slider */}
                                    {activeSlide.imageUrl && (
                                        <div className="p-3 rounded-2xl bg-surface-containerLowest border border-outlineVariant/30 flex items-center gap-4">
                                            <SlidersHorizontal className="w-4 h-4 text-surface-onVariant shrink-0" />
                                            <div className="flex-1 flex items-center gap-3">
                                                <span className="text-[11px] font-bold text-surface-onVariant whitespace-nowrap">
                                                    Helligkeit: {activeSlide.brightness || 100}%
                                                </span>
                                                <input
                                                    type="range"
                                                    min="40"
                                                    max="120"
                                                    step="5"
                                                    value={activeSlide.brightness || 100}
                                                    onChange={(e) => updateActiveSlide('brightness', parseInt(e.target.value))}
                                                    className="w-full accent-primary cursor-pointer"
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Right Side: Live Studio Canvas Preview (5 cols) */}
                <div className="lg:col-span-5 flex flex-col items-center">
                    <div className="sticky top-20 w-full flex flex-col items-center">
                        <div className="w-full flex items-center justify-between mb-3 px-2 flex-wrap gap-2">
                            <span className="text-xs font-bold text-surface-onVariant uppercase tracking-wider flex items-center gap-2">
                                <Eye className="w-4 h-4 text-primary" />
                                <span>Live-Vorschau (4:5 Portrait)</span>
                            </span>
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (activeTheme.isLight) {
                                            const darkMatch = themes.find(t => !t.isLight && t.isStammBrand) || themes.find(t => !t.isLight);
                                            if (darkMatch) setThemeId(darkMatch.id);
                                        } else {
                                            const lightMatch = themes.find(t => t.isLight && t.isStammBrand) || themes.find(t => t.isLight);
                                            if (lightMatch) setThemeId(lightMatch.id);
                                        }
                                    }}
                                    className={`px-2.5 py-1 rounded-xl text-[11px] font-bold border transition-all cursor-pointer flex items-center gap-1.5 shadow-2xs ${
                                        activeTheme.isLight
                                            ? 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30 hover:bg-amber-500/25'
                                            : 'bg-surface-containerHighest text-surface-on border-outlineVariant/40 hover:bg-surface-container'
                                    }`}
                                    title={activeTheme.isLight ? 'Zu dunklem Design wechseln' : 'Zu hellem Design (für helle Fotos) wechseln'}
                                >
                                    {activeTheme.isLight ? <span>☀️ Helles Design</span> : <span>🌙 Dunkles Design</span>}
                                    <span className="opacity-60 text-[10px]">⇄</span>
                                </button>
                                <span className="px-2.5 py-0.5 rounded-full text-xs bg-surface-containerHighest font-bold text-surface-on">
                                    {activeSlideIndex + 1} / {slides.length}
                                </span>
                            </div>
                        </div>

                        {/* The Canvas Studio Mockup Card */}
                        <div className="relative rounded-3xl overflow-hidden shadow-2xl border-4 border-surface-containerHighest bg-black max-w-[340px] sm:max-w-[380px] w-full aspect-[4/5] flex items-center justify-center">
                            <canvas
                                ref={canvasRef}
                                width={CANVAS_WIDTH}
                                height={CANVAS_HEIGHT}
                                className="w-full h-full object-contain"
                            />
                        </div>

                        {/* Carousel Navigation Controller */}
                        <div className="flex items-center gap-4 mt-5">
                            <button
                                onClick={() => setActiveSlideIndex(prev => Math.max(0, prev - 1))}
                                disabled={activeSlideIndex === 0}
                                className="p-3 rounded-full bg-surface-containerLow hover:bg-surface-containerHigh border border-outlineVariant/30 disabled:opacity-30 text-surface-on transition-colors cursor-pointer shadow-sm"
                                title="Vorherige Folie"
                            >
                                <ChevronLeft className="w-5 h-5" />
                            </button>

                            {/* Dots */}
                            <div className="flex items-center gap-1.5">
                                {slides.map((_, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => setActiveSlideIndex(idx)}
                                        className={`h-2 rounded-full cursor-pointer transition-all ${
                                            activeSlideIndex === idx
                                                ? 'w-7 bg-primary shadow-xs'
                                                : 'w-2 bg-outlineVariant/50 hover:bg-outlineVariant'
                                        }`}
                                    />
                                ))}
                            </div>

                            <button
                                onClick={() => setActiveSlideIndex(prev => Math.min(slides.length - 1, prev + 1))}
                                disabled={activeSlideIndex === slides.length - 1}
                                className="p-3 rounded-full bg-surface-containerLow hover:bg-surface-containerHigh border border-outlineVariant/30 disabled:opacity-30 text-surface-on transition-colors cursor-pointer shadow-sm"
                                title="Nächste Folie"
                            >
                                <ChevronRight className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Single Slide Download Button */}
                        <button
                            onClick={handleDownloadSingle}
                            className="mt-4 flex items-center gap-2 text-xs font-bold text-primary hover:underline transition-colors cursor-pointer"
                        >
                            <Download className="w-4 h-4" />
                            <span>Nur Folie #{activeSlideIndex + 1} als HD PNG exportieren</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* BOB AI KARUSSELL GENERATOR MODAL */}
            {isBobEnabled && isAiModalOpen && (
                <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="md3-dialog max-w-lg w-full bg-surface-containerHigh rounded-3xl p-6 border border-outlineVariant/30 shadow-2xl space-y-5 animate-scale-in">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-2xl bg-gradient-to-tr from-primary to-secondary text-primary-on flex items-center justify-center shadow-sm">
                                <Bot className="w-6 h-6" />
                            </div>
                            <div>
                                <h3 className="text-title-large font-bold text-surface-on">Bob: KI-Karussell erstellen</h3>
                                <p className="text-body-small text-surface-onVariant">Bob füllt Folien, Texte, Fotos, Zitate & Zahlen automatisch aus.</p>
                            </div>
                        </div>

                        {/* Source Mode Selector */}
                        <div className="flex items-center gap-2 p-1.5 rounded-2xl bg-surface-containerLowest border border-outlineVariant/30">
                            <button
                                type="button"
                                onClick={() => setAiSourceMode('topic')}
                                className={`flex-1 py-1.5 px-3 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                                    aiSourceMode === 'topic' ? 'bg-primary text-primary-on shadow-xs' : 'text-surface-onVariant hover:text-surface-on'
                                }`}
                            >
                                Freies Thema
                            </button>
                            <button
                                type="button"
                                onClick={() => setAiSourceMode('blog')}
                                className={`flex-1 py-1.5 px-3 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                                    aiSourceMode === 'blog' ? 'bg-primary text-primary-on shadow-xs' : 'text-surface-onVariant hover:text-surface-on'
                                }`}
                            >
                                Aus Blogbeitrag
                            </button>
                            <button
                                type="button"
                                onClick={() => setAiSourceMode('event')}
                                className={`flex-1 py-1.5 px-3 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                                    aiSourceMode === 'event' ? 'bg-primary text-primary-on shadow-xs' : 'text-surface-onVariant hover:text-surface-on'
                                }`}
                            >
                                Aus Event
                            </button>
                        </div>

                        <div className="space-y-4">
                            {/* Theme Style Selector for Bob */}
                            <div>
                                <label className="block text-label-small font-bold text-surface-onVariant mb-1.5">Design-Stil wählen</label>
                                <div className="grid grid-cols-2 gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setAiThemeStyle('light')}
                                        className={`p-2.5 rounded-2xl border text-xs font-bold transition-all flex items-center justify-center gap-2 cursor-pointer ${
                                            aiThemeStyle === 'light'
                                                ? 'border-amber-500 bg-amber-500/15 text-amber-700 dark:text-amber-300 ring-1 ring-amber-500'
                                                : 'border-outlineVariant/30 bg-surface-containerLowest text-surface-onVariant hover:text-surface-on'
                                        }`}
                                    >
                                        <span>☀️ Helles Design</span>
                                        <span className="text-[10px] font-normal opacity-80">(für helle Fotos)</span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setAiThemeStyle('dark')}
                                        className={`p-2.5 rounded-2xl border text-xs font-bold transition-all flex items-center justify-center gap-2 cursor-pointer ${
                                            aiThemeStyle === 'dark'
                                                ? 'border-primary bg-primary/15 text-primary ring-1 ring-primary'
                                                : 'border-outlineVariant/30 bg-surface-containerLowest text-surface-onVariant hover:text-surface-on'
                                        }`}
                                    >
                                        <span>🌙 Dunkles Design</span>
                                        <span className="text-[10px] font-normal opacity-80">(Klassisch)</span>
                                    </button>
                                </div>
                            </div>
                            {aiSourceMode === 'topic' && (
                                <div>
                                    <label className="block text-label-small font-bold text-surface-onVariant mb-1">Thema oder Erlebnisse beschreiben</label>
                                    <textarea
                                        rows={3}
                                        value={aiPrompt}
                                        onChange={(e) => setAiPrompt(e.target.value)}
                                        placeholder="z. B. Unser 7-tägiges Sommerlager 2026 im Schwarzwald mit 38 Pfadis, Jurtenbau, Hajik, Lagerfeuer und Stockbrot..."
                                        className="w-full bg-surface-containerLowest border border-outlineVariant/40 rounded-2xl p-3.5 text-sm text-surface-on focus:outline-none focus:border-primary resize-none"
                                    />

                                    <div className="flex flex-wrap gap-1.5 pt-2">
                                        <span className="text-[11px] font-bold text-surface-onVariant mr-1">Vorschläge:</span>
                                        {[
                                            'Sommerlager im Schwarzwald',
                                            'Wölflinge Schnuppertag',
                                            'Pfingstlager & Jurtenbau',
                                            'Stammes-Hajik durch die Wälder',
                                            'Stammesjubiläum 2026'
                                        ].map((sug, i) => (
                                            <button
                                                key={i}
                                                type="button"
                                                onClick={() => setAiPrompt(sug)}
                                                className="px-2.5 py-1 rounded-xl bg-surface-containerLowest hover:bg-surface-containerHighest border border-outlineVariant/30 text-[11px] font-semibold text-primary cursor-pointer transition-colors"
                                            >
                                                {sug}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {aiSourceMode === 'blog' && (
                                <div className="space-y-2">
                                    <label className="block text-label-small font-bold text-surface-onVariant">Blogbeitrag wählen</label>
                                    <select
                                        value={aiSelectedBlogId}
                                        onChange={(e) => setAiSelectedBlogId(e.target.value)}
                                        className="w-full bg-surface-containerLowest border border-outlineVariant/40 rounded-2xl p-3 text-sm text-surface-on font-semibold focus:outline-none focus:border-primary"
                                    >
                                        {blogPosts.map(p => (
                                            <option key={p._id} value={p._id}>{p.title}</option>
                                        ))}
                                    </select>
                                    <p className="text-[11px] text-surface-onVariant">
                                        Bob liest den Text und nutzt automatisch die hochgeladenen Bilder dieses Blogartikels.
                                    </p>
                                </div>
                            )}

                            {aiSourceMode === 'event' && (
                                <div className="space-y-2">
                                    <label className="block text-label-small font-bold text-surface-onVariant">Event / Termin wählen</label>
                                    <select
                                        value={aiSelectedEventId}
                                        onChange={(e) => setAiSelectedEventId(e.target.value)}
                                        className="w-full bg-surface-containerLowest border border-outlineVariant/40 rounded-2xl p-3 text-sm text-surface-on font-semibold focus:outline-none focus:border-primary"
                                    >
                                        {events.map(e => (
                                            <option key={e._id} value={e._id}>{e.title}</option>
                                        ))}
                                    </select>
                                    <p className="text-[11px] text-surface-onVariant">
                                        Bob extrahiert Datum, Ort, Zielgruppe und packende Motivations-Folien.
                                    </p>
                                </div>
                            )}

                            {/* Bob Special Slide Checkboxes */}
                            <div className="p-3.5 rounded-2xl bg-surface-containerLowest border border-outlineVariant/30 space-y-2.5">
                                <span className="text-xs font-bold text-surface-on block">Spezial-Folien einbauen:</span>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                    <label className="flex items-center gap-2 text-xs text-surface-on cursor-pointer select-none">
                                        <input
                                            type="checkbox"
                                            checked={aiIncludeStats}
                                            onChange={(e) => setAiIncludeStats(e.target.checked)}
                                            className="rounded border-outlineVariant text-primary focus:ring-primary accent-primary"
                                        />
                                        <span>Zahlen & Fakten</span>
                                    </label>
                                    <label className="flex items-center gap-2 text-xs text-surface-on cursor-pointer select-none">
                                        <input
                                            type="checkbox"
                                            checked={aiIncludeQuote}
                                            onChange={(e) => setAiIncludeQuote(e.target.checked)}
                                            className="rounded border-outlineVariant text-primary focus:ring-primary accent-primary"
                                        />
                                        <span>Zitat-Folie</span>
                                    </label>
                                    <label className="flex items-center gap-2 text-xs text-surface-on cursor-pointer select-none">
                                        <input
                                            type="checkbox"
                                            checked={aiIncludeEvent}
                                            onChange={(e) => setAiIncludeEvent(e.target.checked)}
                                            className="rounded border-outlineVariant text-primary focus:ring-primary accent-primary"
                                        />
                                        <span>Termin-Folie</span>
                                    </label>
                                </div>
                            </div>

                            {aiStatusMessage && (
                                <p className="text-xs text-primary font-bold animate-pulse flex items-center gap-2">
                                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                                    <span>{aiStatusMessage}</span>
                                </p>
                            )}
                        </div>

                        <div className="flex items-center justify-end gap-3 pt-4 border-t border-outlineVariant/20">
                            <button
                                type="button"
                                onClick={() => setIsAiModalOpen(false)}
                                className="md3-btn-text text-surface-onVariant cursor-pointer"
                            >
                                Abbrechen
                            </button>
                            <button
                                type="button"
                                onClick={() => handleRunBobAiGenerator()}
                                disabled={isAiGenerating || (aiSourceMode === 'topic' && !aiPrompt.trim())}
                                className="md3-btn-filled flex items-center gap-2 px-5 cursor-pointer disabled:opacity-50"
                            >
                                {isAiGenerating ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                                <span>{isAiGenerating ? 'Bob zaubert...' : 'Karussell erstellen'}</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InstagramGenerator;
